import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  BookOpen,
  Braces,
  Check,
  ChevronDown,
  CircleHelp,
  Cloud,
  Code2,
  Copy,
  Database,
  Eye,
  FileText,
  Flag,
  Folder,
  GitBranch,
  History,
  KeyRound,
  LayoutDashboard,
  Link2,
  LockKeyhole,
  Menu,
  MessageSquare,
  Moon,
  MoreHorizontal,
  Network,
  Palette,
  Play,
  Plus,
  Redo2,
  Rocket,
  Save,
  Search,
  Settings,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Sun,
  Tags,
  Trash2,
  Undo2,
  Users,
  WandSparkles,
  Workflow,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { api, slugify } from "./api.js";
import { mergeStoryDocument, sameValue } from "./collaboration.js";
import { deletePassage, duplicatePassage, makeStartingPassage } from "./storyDocument.js";
import { Markdown } from "./Markdown.jsx";

const presets = [
  ["midnight", "Midnight", "dark", "#8b5cf6"],
  ["obsidian", "Obsidian", "dark", "#38bdf8"],
  ["void", "The Void", "dark", "#d946ef"],
  ["ember", "Ember", "dark", "#f97316"],
  ["forest", "Deep Forest", "dark", "#34d399"],
  ["ocean", "Abyssal", "dark", "#22d3ee"],
  ["rose", "After Hours", "dark", "#fb7185"],
  ["terminal", "Terminal", "dark", "#84cc16"],
  ["paper", "Paper", "light", "#4f46e5"],
  ["parchment", "Parchment", "light", "#b45309"],
  ["cloud", "Cloud", "light", "#0284c7"],
  ["blossom", "Blossom", "light", "#db2777"],
].map(([id, name, mode, accent]) => ({ id, name, mode, accent }));

const demoDocument = {
  format: 1,
  passages: [
    {
      id: "start",
      title: "The Signal",
      body: "The radio clicks on at 3:17 AM. Through the static, someone whispers your name.",
      x: 80,
      y: 210,
      links: [{ label: "Answer the radio", target: "answer", condition: "" }],
      tags: ["opening"],
    },
    {
      id: "answer",
      title: "Answer the radio",
      body: "You press the transmit key. The static stops.\n\n“I knew you were still there,” the voice says.",
      x: 410,
      y: 100,
      links: [
        {
          label: "Ask who they are",
          target: "voice",
          condition: "courage >= 1",
        },
      ],
      tags: ["radio"],
    },
    {
      id: "leave",
      title: "Leave the house",
      body: "You reach for the door. The deadbolt turns itself.",
      x: 410,
      y: 340,
      links: [{ label: "Run upstairs", target: "voice", condition: "" }],
      tags: ["house"],
    },
    {
      id: "voice",
      title: "The Voice",
      body: "“The person who taught this house how to remember.”",
      x: 760,
      y: 210,
      links: [],
      tags: ["ending"],
    },
  ],
  variables: [
    {
      id: "var-courage",
      name: "courage",
      type: "number",
      defaultValue: 1,
      description: "Tracks brave choices.",
    },
  ],
  styles: { readerTheme: "midnight", transition: "fade" },
};

function Button({ children, className = "", variant = "default", ...props }) {
  return (
    <button type={props.type || "button"} className={`button ${variant} ${className}`} {...props}>
      {children}
    </button>
  );
}

const operatorOptions = {
  boolean: [["is_true", "is true"], ["is_false", "is false"]],
  number: [["eq", "equals"], ["neq", "does not equal"], ["gt", "is greater than"], ["gte", "is at least"], ["lt", "is less than"], ["lte", "is at most"]],
  string: [["eq", "equals"], ["neq", "does not equal"], ["contains", "contains"], ["not_contains", "does not contain"]],
  list: [["contains", "contains"], ["not_contains", "does not contain"]],
};
const effectOptions = {
  boolean: [["set_true", "set true"], ["set_false", "set false"], ["toggle", "toggle"]],
  number: [["set", "set to"], ["add", "increase by"], ["subtract", "decrease by"]],
  string: [["set", "set to"]],
  list: [["add_item", "add item"], ["remove_item", "remove item"]],
};

function conditionRule(link, variables) {
  if (link.conditionRule) return link.conditionRule;
  const legacy = String(link.condition || "").trim();
  if (!legacy) return { mode: "always", variableId: "", operator: "", value: "" };
  const match = legacy.match(/^([A-Za-z_][\w]*)\s*(==|!=|>=|<=|>|<|contains)\s*(.*)$/);
  if (!match) return { mode: "always", variableId: "", operator: "", value: "" };
  const variable = variables.find((item) => item.name === match[1]);
  const operators = { "==": "eq", "!=": "neq", ">": "gt", ">=": "gte", "<": "lt", "<=": "lte", contains: "contains" };
  const rawValue = match[3].replace(/^['\"]|['\"]$/g, "");
  const operator = variable?.type === "boolean" && match[2] === "==" ? (rawValue === "true" ? "is_true" : "is_false") : operators[match[2]];
  return { mode: "when", variableId: variable?.id || "", operator, value: rawValue };
}

function conditionMatches(link, variables, values) {
  const rule = conditionRule(link, variables);
  if (rule.mode !== "when") return true;
  const variable = variables.find((item) => item.id === rule.variableId);
  if (!variable) return false;
  const actual = values[variable.id];
  const expected = variable.type === "number" ? Number(rule.value) : variable.type === "boolean" ? rule.operator === "is_true" : rule.value;
  if (rule.operator === "is_true") return Boolean(actual) === true;
  if (rule.operator === "is_false") return Boolean(actual) === false;
  if (rule.operator === "eq") return actual == expected;
  if (rule.operator === "neq") return actual != expected;
  if (rule.operator === "gt") return Number(actual) > Number(expected);
  if (rule.operator === "gte") return Number(actual) >= Number(expected);
  if (rule.operator === "lt") return Number(actual) < Number(expected);
  if (rule.operator === "lte") return Number(actual) <= Number(expected);
  if (rule.operator === "contains") return Array.isArray(actual) ? actual.includes(expected) : String(actual ?? "").includes(String(expected));
  if (rule.operator === "not_contains") return Array.isArray(actual) ? !actual.includes(expected) : !String(actual ?? "").includes(String(expected));
  return false;
}

function applyChoiceEffect(link, variables, values) {
  const effect = link.effectRule;
  if (!effect?.variableId) return values;
  const variable = variables.find((item) => item.id === effect.variableId);
  if (!variable) return values;
  const next = { ...values }, current = next[variable.id];
  if (effect.operation === "set_true") next[variable.id] = true;
  else if (effect.operation === "set_false") next[variable.id] = false;
  else if (effect.operation === "toggle") next[variable.id] = !Boolean(current);
  else if (effect.operation === "set") next[variable.id] = variable.type === "number" ? Number(effect.value) : effect.value;
  else if (effect.operation === "add") next[variable.id] = Number(current || 0) + Number(effect.value || 0);
  else if (effect.operation === "subtract") next[variable.id] = Number(current || 0) - Number(effect.value || 0);
  else if (effect.operation === "add_item") next[variable.id] = [...new Set([...(Array.isArray(current) ? current : []), effect.value])];
  else if (effect.operation === "remove_item") next[variable.id] = (Array.isArray(current) ? current : []).filter((item) => item !== effect.value);
  return next;
}
function Badge({ children, tone = "" }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}
function Avatar({ name = "Casey", image }) {
  return image ? (
    <img className="avatar" src={image} alt="" />
  ) : (
    <span className="avatar">
      {name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)}
    </span>
  );
}

function BootScreen() {
  return (
    <div className="boot-screen">
      <div className="brand-icon">
        <GitBranch />
      </div>
      <h1>Storyweave</h1>
      <span className="loader" />
    </div>
  );
}

function LoginPage({ session }) {
  const query = new URLSearchParams(location.search);
  const oidcError = query.get("error");
  const oidcErrorDescription = query.get("error_description");
  const oidcCallbackUrl = query.get("callback_url");
  const oidcProviderStatus = query.get("provider_status");
  const authComplete = query.get("auth") === "complete";
  useEffect(() => {
    const url = new URL(location.href);
    if (url.searchParams.get("sso") === "none") {
      sessionStorage.setItem("storyweave-sso-attempt", "complete");
      url.searchParams.delete("sso");
      history.replaceState({}, "", url.pathname + url.search);
      return;
    }
    if (url.searchParams.get("auth") === "complete") {
      sessionStorage.setItem("storyweave-sso-attempt", "complete");
      return;
    }
    if (!sessionStorage.getItem("storyweave-sso-attempt")) {
      sessionStorage.setItem("storyweave-sso-attempt", "1");
      location.assign(
        "/auth/login?silent=1&returnTo=" + encodeURIComponent("/"),
      );
    }
  }, []);
  return (
    <div className="auth-page">
      <div className="auth-art">
        <div className="constellation">
          <span />
          <span />
          <span />
          <span />
        </div>
        <div>
          <Badge>STORYWEAVE</Badge>
          <h1>Every choice leaves a thread.</h1>
          <p>
            Write, connect, test, and publish interactive stories with your
            whole team.
          </p>
        </div>
      </div>
      <div className="auth-card">
        <div className="brand-icon">
          <GitBranch />
        </div>
        <h2>Welcome back</h2>
        <p>Continue with your organization’s identity provider.</p>
        {oidcError && (
          <div className="error-banner oidc-error" role="alert">
            <strong>SSO sign-in failed: {oidcError}</strong>
            <span>{oidcErrorDescription || "No error description was returned by the identity provider."}</span>
            {oidcProviderStatus && <code>Provider HTTP status: {oidcProviderStatus}</code>}
            {oidcCallbackUrl && <code>Callback URI: {oidcCallbackUrl}</code>}
          </div>
        )}
        {authComplete && !session?.authenticated && (
          <div className="error-banner oidc-error" role="alert">
            <strong>SSO succeeded, but the Storyweave session did not persist.</strong>
            <span>Storyweave stopped the automatic login loop. Local session reason:</span>
            <code>{session?.reason || "session_unavailable"}</code>
          </div>
        )}
        <Button onClick={() => {
          sessionStorage.setItem("storyweave-sso-attempt", "manual");
          location.assign("/auth/login?returnTo=/");
        }}>
          <KeyRound /> Sign in with SSO
        </Button>
        <small>Access is managed by your Storyweave administrator.</small>
      </div>
    </div>
  );
}

function SetupPage() {
  const [form, setForm] = useState({
    setupKey: "",
    tenantName: "My Studio",
    tenantSlug: "my-studio",
    hostname: location.hostname,
    canonicalUrl: location.origin,
    issuer: "",
    clientId: "storyweave",
    clientSecret: "",
    theme: "midnight",
    rotationDays: 60,
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const update = (key, value) =>
    setForm((state) => ({ ...state, [key]: value }));
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/setup/initialize", {
        method: "POST",
        body: JSON.stringify(form),
      });
      location.assign("/auth/login?returnTo=/");
    } catch (cause) {
      setError(cause.message);
      setBusy(false);
    }
  }
  return (
    <div className="setup-page">
      <div className="setup-aside">
        <div className="brand-lock">
          <div className="brand-icon">
            <GitBranch />
          </div>
          <span>Storyweave</span>
        </div>
        <div>
          <Badge>FIRST-RUN SETUP</Badge>
          <h1>
            Your stories.
            <br />
            Your infrastructure.
          </h1>
          <p>
            This one-time setup creates the first tenant, private workspace,
            role mapping, encryption keyring, and OIDC connection.
          </p>
        </div>
        <ul>
          <li>
            <Shield /> AES-256-GCM data encryption
          </li>
          <li>
            <Network /> Multi-tenant hostname routing
          </li>
          <li>
            <KeyRound /> Keycloak-compatible OIDC and SSO
          </li>
          <li>
            <Activity /> SSE session invalidation
          </li>
        </ul>
      </div>
      <form className="setup-form" onSubmit={submit}>
        <header>
          <h2>Set up Storyweave</h2>
          <p>
            No environment file is needed. You can edit these settings later.
          </p>
        </header>
        {error && <div className="error-banner">{error}</div>}
        <div className="form-section">
          <h3>Studio</h3>
          <div className="form-grid">
            <label>
              Studio name
              <input
                value={form.tenantName}
                onChange={(e) => {
                  update("tenantName", e.target.value);
                  update("tenantSlug", slugify(e.target.value));
                }}
              />
            </label>
            <label>
              Tenant slug
              <input
                value={form.tenantSlug}
                onChange={(e) => update("tenantSlug", e.target.value)}
              />
            </label>
            <label>
              Canonical hostname
              <input
                value={form.hostname}
                onChange={(e) => update("hostname", e.target.value)}
              />
            </label>
            <label>
              Canonical URL
              <input
                value={form.canonicalUrl}
                onChange={(e) => update("canonicalUrl", e.target.value)}
              />
            </label>
          </div>
        </div>
        <div className="form-section">
          <h3>OIDC connection</h3>
          <div className="form-grid">
            <label className="wide">
              Issuer URL
              <input
                placeholder="https://auth.example.com/realms/studio"
                value={form.issuer}
                onChange={(e) => update("issuer", e.target.value)}
                required
              />
            </label>
            <label>
              Client ID
              <input
                value={form.clientId}
                onChange={(e) => update("clientId", e.target.value)}
                required
              />
            </label>
            <label>
              Client secret
              <input
                type="password"
                value={form.clientSecret}
                onChange={(e) => update("clientSecret", e.target.value)}
              />
            </label>
            <div className="access-note wide">
              <KeyRound />
              <span><strong>Administrator bootstrap</strong>The Keycloak realm role <code>app-superadmin</code> becomes platform administrator on the initial tenant. On future tenants it becomes that tenant’s administrator.</span>
            </div>
          </div>
        </div>
        <div className="form-section">
          <h3>Security</h3>
          <div className="form-grid">
            <label>
              Setup key
              <input
                type="password"
                value={form.setupKey}
                onChange={(e) => update("setupKey", e.target.value)}
                required
              />
            </label>
            <label>
              Key rotation
              <select
                value={form.rotationDays}
                onChange={(e) => update("rotationDays", Number(e.target.value))}
              >
                <option value="30">Every 30 days</option>
                <option value="45">Every 45 days</option>
                <option value="60">Every 60 days</option>
                <option value="90">Every 90 days</option>
              </select>
            </label>
          </div>
          <p className="field-help">
            The setup key is printed once in the application console. Encryption
            keys remain outside the database with restricted file permissions.
          </p>
        </div>
        <div className="setup-actions">
          <Button type="submit" disabled={busy}>
            {busy ? "Creating your studio…" : "Create studio"}{" "}
            <ArrowLeft className="next-arrow" />
          </Button>
        </div>
      </form>
    </div>
  );
}

function OidcRecoveryPage() {
  const [form, setForm] = useState({
    setupKey: "",
    tenantSlug: "",
    canonicalUrl: location.origin,
    issuer: "",
    clientId: "storyweave",
    clientSecret: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/setup/repair-oidc", { method: "POST", body: JSON.stringify(form) });
      sessionStorage.removeItem("storyweave-sso-attempt");
      location.assign("/auth/login?returnTo=/");
    } catch (cause) {
      setError(cause.message);
      setBusy(false);
    }
  }
  return (
    <div className="setup-page recovery-page">
      <div className="setup-aside">
        <div className="brand-lock">
          <div className="brand-icon"><GitBranch /></div><span>Storyweave</span>
        </div>
        <div>
          <Badge>IDENTITY RECOVERY</Badge>
          <h1>Repair SSO.<br />Keep everything else.</h1>
          <p>This changes only the selected tenant’s OIDC connection. Workspaces, stories, permissions, encryption keys, and versions are untouched.</p>
        </div>
        <ul>
          <li><Check /> Accepts a realm URL or full Keycloak authorization URL</li>
          <li><Check /> Removes protocol endpoints and query parameters automatically</li>
          <li><Check /> Preserves the existing client secret when left blank</li>
        </ul>
      </div>
      <form className="setup-form" onSubmit={submit}>
        <header><h2>Repair OIDC configuration</h2><p>Use the original setup key. It remains in <code>data/setup.used</code> on the server.</p></header>
        {error && <div className="error-banner">{error}</div>}
        <div className="form-section">
          <h3>Tenant and application</h3>
          <div className="form-grid">
            <label>Tenant slug<input value={form.tenantSlug} onChange={(event) => update("tenantSlug", event.target.value)} placeholder="edgefox" required /></label>
            <label>Client ID<input value={form.clientId} onChange={(event) => update("clientId", event.target.value)} required /></label>
            <label className="wide">Canonical Storyweave URL<input value={form.canonicalUrl} onChange={(event) => update("canonicalUrl", event.target.value)} required /></label>
          </div>
        </div>
        <div className="form-section">
          <h3>Identity provider</h3>
          <div className="form-grid">
            <label className="wide">Issuer or Keycloak SSO URL<input value={form.issuer} onChange={(event) => update("issuer", event.target.value)} placeholder="https://auth.example.com/realms/edgefox" required /></label>
            <label>Client secret<input type="password" value={form.clientSecret} onChange={(event) => update("clientSecret", event.target.value)} placeholder="Leave blank to preserve" /></label>
            <label>Original setup key<input type="password" value={form.setupKey} onChange={(event) => update("setupKey", event.target.value)} required /></label>
          </div>
        </div>
        <div className="setup-actions"><Button type="submit" disabled={busy}>{busy ? "Repairing connection…" : "Save and test sign-in"} <ArrowLeft className="next-arrow" /></Button></div>
      </form>
    </div>
  );
}

function useRealtime(refreshSession) {
  useEffect(() => {
    const stream = new EventSource("/auth/events");
    const invalidated = (event) => {
      let reason = "session_invalidated";
      try { reason = JSON.parse(event.data)?.reason || reason; } catch {}
      const query = new URLSearchParams({
        sso: "none",
        error: "session_invalidated",
        error_description: `Storyweave ended the local session: ${reason}`,
      });
      location.assign(`/login?${query}`);
    };
    const storyUpdated = (event) => {
      try {
        window.dispatchEvent(
          new CustomEvent("storyweave:story-updated", {
            detail: JSON.parse(event.data),
          }),
        );
      } catch {}
    };
    const storyPresence = (event) => {
      try {
        window.dispatchEvent(new CustomEvent("storyweave:story-presence", { detail: JSON.parse(event.data) }));
      } catch {}
    };
    const storyPublished = (event) => {
      try {
        window.dispatchEvent(new CustomEvent("storyweave:story-published", { detail: JSON.parse(event.data) }));
      } catch {}
    };
    stream.addEventListener("session-invalidated", invalidated);
    stream.addEventListener("story-updated", storyUpdated);
    stream.addEventListener("story-presence", storyPresence);
    stream.addEventListener("story-published", storyPublished);
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") refreshSession();
    }, 120000);
    const focus = () => refreshSession();
    window.addEventListener("focus", focus);
    return () => {
      stream.close();
      clearInterval(poll);
      window.removeEventListener("focus", focus);
    };
  }, [refreshSession]);
}

function Shell({ session, refreshSession, children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  useRealtime(refreshSession);
  const navigation = [
    ["/", LayoutDashboard, "Home"],
    ["/workspaces", Folder, "Workspaces"],
    ["/recent", History, "Recent"],
    ["/themes", Palette, "Themes"],
    ["/docs", BookOpen, "Documentation"],
  ];
  const admin = session.systemRoles?.some((role) =>
    ["platform-admin", "tenant-admin"].includes(role),
  );
  return (
    <div className={`app-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <aside className="sidebar">
        <div className="brand-lock">
          <div className="brand-icon">
            <GitBranch />
          </div>
          {!collapsed && (
            <>
              <span>Storyweave</span>
              <Badge>1.2.0</Badge>
            </>
          )}
        </div>
        <Button variant="accent" className="new-button" onClick={() => navigate("/workspaces")}>
          <Plus />
          {!collapsed && "New story"}
        </Button>
        <nav>
          {navigation.map(([to, Icon, label]) => (
            <NavLink key={to} to={to} end={to === "/"}>
              <Icon />
              {!collapsed && label}
            </NavLink>
          ))}
          {admin && (
            <NavLink to="/admin">
              <Shield />
              {!collapsed && "Administration"}
            </NavLink>
          )}
        </nav>
        <div className="sidebar-bottom">
          <button onClick={() => setCollapsed(!collapsed)}>
            <Menu />
            {!collapsed && "Collapse sidebar"}
          </button>
          <button className={`profile ${profileOpen ? "open" : ""}`} aria-expanded={profileOpen} onClick={() => setProfileOpen(!profileOpen)}>
            <Avatar
              name={session.user?.display_name || session.user?.username}
              image={session.user?.avatar_url}
            />
            {!collapsed && (
              <span>
                <strong>
                  {session.user?.display_name || session.user?.username}
                </strong>
                <small>{session.tenant?.name}</small>
              </span>
            )}
            <ChevronDown />
          </button>
          {profileOpen && !collapsed && <div className="profile-menu">
            <div><strong>{session.user?.display_name || session.user?.username}</strong><small>{session.systemRoles?.join(", ") || "Storyweave user"}</small></div>
            {admin && <button onClick={() => { navigate("/admin"); setProfileOpen(false); }}><Settings /> Tenant settings</button>}
            <button onClick={() => { navigate("/themes"); setProfileOpen(false); }}><Palette /> Appearance</button>
            <button onClick={() => { navigate("/docs"); setProfileOpen(false); }}><BookOpen /> Documentation</button>
            <form action="/auth/logout" method="post"><button type="submit"><ArrowLeft /> Sign out</button></form>
          </div>}
        </div>
      </aside>
      <section className="main-surface">{children}</section>
    </div>
  );
}

function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <header className="page-header">
      <div>
        <span>{eyebrow}</span>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div>{actions}</div>}
    </header>
  );
}

function FormDialog({ title, children, submitLabel = "Create", onSubmit, close, error }) {
  return <div className="modal-backdrop" onMouseDown={close}>
    <form className="form-dialog" onSubmit={onSubmit} onMouseDown={(e) => e.stopPropagation()}>
      <header><div><h2>{title}</h2></div><Button variant="icon" onClick={close}><X /></Button></header>
      <div className="dialog-body">{children}{error && <div className="error-banner">{error}</div>}</div>
      <footer><Button variant="ghost" onClick={close}>Cancel</Button><Button type="submit"><Save /> {submitLabel}</Button></footer>
    </form>
  </div>;
}

function HomePage({ session }) {
  const [workspaces, setWorkspaces] = useState([]), [stories, setStories] = useState([]), [totals, setTotals] = useState({ stories: 0, passages: 0, published: 0, workspaces: 0 });
  const navigate = useNavigate();
  useEffect(() => {
    Promise.all([api("/workspaces"), api("/stories/recent")])
      .then(([workspaceData, storyData]) => { setWorkspaces(workspaceData.workspaces); setStories(storyData.stories); setTotals(storyData.totals); })
      .catch(() => {});
  }, []);
  return (
    <div className="page">
      <PageHeader
        eyebrow="GOOD EVENING"
        title={
          session.user?.display_name?.split(" ")[0]
            ? `Ready to weave, ${session.user.display_name.split(" ")[0]}?`
            : "Ready to weave?"
        }
        description="Pick up where you left off or start a new thread."
        actions={
          <>
            <Button variant="ghost" aria-label="Search stories" onClick={() => navigate("/workspaces")}>
              <Search />
            </Button>
            <Button onClick={() => navigate("/workspaces")}>
              <Plus /> New story
            </Button>
          </>
        }
      />
      <section className="stat-grid">
        <article>
          <span>
            <FileText /> STORIES
          </span>
          <strong>{totals.stories}</strong>
          <small>Stories you can access</small>
        </article>
        <article>
          <span>
            <GitBranch /> PASSAGES
          </span>
          <strong>{totals.passages}</strong>
          <small>Across recent stories</small>
        </article>
        <article>
          <span>
            <Folder /> WORKSPACES
          </span>
          <strong>{totals.workspaces}</strong>
          <small>Containing visible stories</small>
        </article>
        <article>
          <span>
            <Rocket /> PUBLISHED
          </span>
          <strong>{totals.published}</strong>
          <small>Live published snapshots</small>
        </article>
      </section>
      <section className="content-section">
        <div className="section-heading">
          <div>
            <h2>Continue writing</h2>
            <p>Your most recently edited stories</p>
          </div>
          <Link to="/workspaces">View all</Link>
        </div>
        <div className="story-grid">
          {stories.map((story) => <StoryCard key={story.id} id={story.id} title={story.title} workspace={story.workspace} passages={story.passages} updated={new Date(story.updatedAt).toLocaleString()} theme={story.theme} published={story.published} />)}
          <button className="create-card" onClick={() => navigate("/workspaces")}>
            <Plus />
            <strong>Create a story</strong>
            <span>Start from scratch or a template</span>
          </button>
        </div>
      </section>
      <section className="content-section">
        <div className="section-heading">
          <div>
            <h2>Workspaces</h2>
            <p>Stories organized around your teams and projects</p>
          </div>
        </div>
        <div className="workspace-strip">
          {(workspaces.length
            ? workspaces
            : [
                {
                  id: "demo",
                  name: "Story Studio",
                  description: "Default private workspace",
                },
              ]
          ).map((workspace) => (
            <Link
              to={`/workspaces/${workspace.id}`}
              className="workspace-card"
              key={workspace.id}
            >
              <span className="folder-icon">
                <Folder />
              </span>
              <span>
                <strong>{workspace.name}</strong>
                <small>{workspace.description || "Private workspace"}</small>
              </span>
              <Badge>
                <LockKeyhole /> PRIVATE
              </Badge>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function StoryCard({ id, title, workspace, passages, updated, theme, published }) {
  return (
    <Link to={`/stories/${id}`} className={`story-card theme-preview ${theme}`}>
      <div className="story-card-art">
        <span />
        <span />
        <span />
        <GitBranch />
      </div>
      <div className="story-card-body">
        <div>
          <Badge>{workspace}</Badge>
          <MoreHorizontal />
        </div>
        <h3>{title}</h3>
        <p>
          <GitBranch /> {passages} passages <span>·</span> {updated} {published && <Badge tone="success">Published</Badge>}
        </p>
      </div>
    </Link>
  );
}

function WorkspacesPage() {
  const [workspaces, setWorkspaces] = useState([]);
  const [creating, setCreating] = useState(false), [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", slug: "", description: "", visibility: "private" });
  const load = () => api("/workspaces").then((data) => setWorkspaces(data.workspaces));
  useEffect(() => {
    load();
  }, []);
  const create = async (e) => {
    e.preventDefault(); setError("");
    try { await api("/workspaces", { method: "POST", body: JSON.stringify({ ...form, slug: form.slug || slugify(form.name) }) }); setCreating(false); setForm({ name: "", slug: "", description: "", visibility: "private" }); load(); }
    catch (cause) { setError(cause.message); }
  };
  return (
    <div className="page">
      <PageHeader
        eyebrow="LIBRARY"
        title="Workspaces"
        description="Separate stories by team, project, or production while keeping access explicit."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> New workspace
          </Button>
        }
      />
      <div className="workspace-grid">
        {workspaces.map((workspace) => (
          <Link
            to={`/workspaces/${workspace.id}`}
            className="workspace-large"
            key={workspace.id}
          >
            <header>
              <span className="folder-icon">
                <Folder />
              </span>
              <Badge>
                <LockKeyhole /> {workspace.visibility.toUpperCase()}
              </Badge>
            </header>
            <h2>{workspace.name}</h2>
            <p>{workspace.description}</p>
            <footer>
              <span>
                <FileText /> 1 story
              </span>
              <span>
                <Users /> Private membership
              </span>
            </footer>
          </Link>
        ))}
        {!workspaces.length && (
          <div className="empty-state">
            <Folder />
            <h2>No visible workspaces</h2>
            <p>
              Ask a workspace administrator to grant access through an OIDC
              group, role, or user rule.
            </p>
          </div>
        )}
      </div>
      {creating && <FormDialog title="New workspace" submitLabel="Create workspace" onSubmit={create} close={() => setCreating(false)} error={error}>
        <label>Name<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugify(e.target.value) })} /></label>
        <label>Slug<input required value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} /></label>
        <label>Description<textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
        <label>Visibility<select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}><option value="private">Private</option><option value="tenant">Entire tenant</option></select></label>
      </FormDialog>}
    </div>
  );
}

function WorkspacePage() {
  const { workspaceId } = useParams();
  const [stories, setStories] = useState([]);
  const [workspace, setWorkspace] = useState(null), [creating, setCreating] = useState(false), [accessOpen, setAccessOpen] = useState(false);
  const [query, setQuery] = useState(""), [visibilityFilter, setVisibilityFilter] = useState("all"), [error, setError] = useState("");
  const [form, setForm] = useState({ title: "", slug: "", theme: "midnight" });
  const navigate = useNavigate();
  const load = () => Promise.all([api(`/workspaces/${workspaceId}`), api(`/workspaces/${workspaceId}/stories`)]).then(([workspaceData, storyData]) => { setWorkspace(workspaceData.workspace); setStories(storyData.stories); });
  useEffect(() => {
    load();
  }, [workspaceId]);
  const create = async (e) => { e.preventDefault(); setError(""); try { const result = await api(`/workspaces/${workspaceId}/stories`, { method: "POST", body: JSON.stringify({ ...form, slug: form.slug || slugify(form.title) }) }); navigate(`/stories/${result.id}`); } catch (cause) { setError(cause.message); } };
  const visibleStories = stories.filter((story) => story.title.toLowerCase().includes(query.toLowerCase()) && (visibilityFilter === "all" || story.visibility === visibilityFilter));
  return (
    <div className="page">
      <PageHeader
        eyebrow="WORKSPACE"
        title={workspace?.name || "Workspace"}
        description={workspace?.description || `${workspace?.visibility || "Private"} workspace`}
        actions={
          <>
            <Button variant="ghost" onClick={() => setAccessOpen(true)}>
              <Users /> Manage access
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Plus /> New story
            </Button>
          </>
        }
      />
      <div className="table-card">
        <div className="table-toolbar">
          <div className="search-field">
            <Search />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search this workspace…" />
          </div>
          <label className="inline-filter"><SlidersHorizontal /><select value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value)}><option value="all">All visibility</option><option value="private">Private</option><option value="workspace">Workspace</option><option value="tenant">Tenant</option></select></label>
        </div>
        <table>
          <thead>
            <tr>
              <th>Story</th>
              <th>Visibility</th>
              <th>Version</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleStories.map((story) => (
              <tr key={story.id}>
                <td>
                  <Link to={`/stories/${story.id}`}>
                    <span className="doc-icon">
                      <FileText />
                    </span>
                    <strong>{story.title}</strong>
                  </Link>
                </td>
                <td>
                  <Badge>
                    <LockKeyhole /> {story.visibility}
                  </Badge>
                </td>
                <td>v{story.version}</td>
                <td>{new Date(story.updated_at).toLocaleString()}</td>
                <td>
                  <MoreHorizontal />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && <FormDialog title="New story" submitLabel="Create story" onSubmit={create} close={() => setCreating(false)} error={error}>
        <label>Title<input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: slugify(e.target.value) })} /></label>
        <label>Slug<input required value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} /></label>
        <label>Reader theme<select value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })}>{presets.map((theme) => <option value={theme.id} key={theme.id}>{theme.name}</option>)}</select></label>
      </FormDialog>}
      {accessOpen && <WorkspaceAccessPanel workspaceId={workspaceId} close={() => setAccessOpen(false)} onSaved={load} />}
    </div>
  );
}

function EditorPage({ session }) {
  const { storyId } = useParams();
  const [story, setStory] = useState(null),
    [document, setDocument] = useState(demoDocument);
  const [selectedId, setSelectedId] = useState("start"),
    [panel, setPanel] = useState("write"),
    [saved, setSaved] = useState(true),
    [zoom, setZoom] = useState(0.86),
    [pan, setPan] = useState({ x: 20, y: 20 }),
    [testing, setTesting] = useState(false),
    [testId, setTestId] = useState("start"),
    [accessOpen, setAccessOpen] = useState(false),
    [remoteVersion, setRemoteVersion] = useState(null),
    [passageSearch, setPassageSearch] = useState(""),
    [presence, setPresence] = useState([]),
    [presenceOpen, setPresenceOpen] = useState(false),
    [contextMenu, setContextMenu] = useState(null),
    [deleteTarget, setDeleteTarget] = useState(null),
    [linkSourceId, setLinkSourceId] = useState(null),
    [mergeConflict, setMergeConflict] = useState(null),
    [syncMessage, setSyncMessage] = useState(""),
    [published, setPublished] = useState(null);
  const saveTimer = useRef();
  const versionRef = useRef(1);
  const undoRef = useRef([]), redoRef = useRef([]), panDrag = useRef(null), nodeDrag = useRef(null);
  const documentRef = useRef(demoDocument), baseDocumentRef = useRef(demoDocument), savedRef = useRef(true), remoteSyncRef = useRef(null), syncInFlight = useRef(false);
  useEffect(() => { documentRef.current = document; }, [document]);
  useEffect(() => { savedRef.current = saved; }, [saved]);
  useEffect(() => {
    if (!linkSourceId) return;
    const cancel = (event) => { if (event.key === "Escape") setLinkSourceId(null); };
    window.addEventListener("keydown", cancel);
    return () => window.removeEventListener("keydown", cancel);
  }, [linkSourceId]);
  useEffect(() => {
    if (!contextMenu) return;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      setContextMenu(null);
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", close);
    window.addEventListener("resize", close);
    window.addEventListener("blur", close);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("blur", close);
    };
  }, [contextMenu]);
  const loadStory = async () => {
    if (storyId === "demo") return;
    const data = await api(`/stories/${storyId}`);
    setStory(data.story);
    setPublished(data.story.published);
    setDocument(data.story.document);
    documentRef.current = data.story.document;
    baseDocumentRef.current = structuredClone(data.story.document);
    setSelectedId(data.story.document.passages[0]?.id);
    versionRef.current = data.story.version;
    setRemoteVersion(null);
    setSaved(true);
    savedRef.current = true;
    setMergeConflict(null);
    setLinkSourceId(null);
    undoRef.current = [];
    redoRef.current = [];
  };
  useEffect(() => {
    loadStory();
  }, [storyId]);
  useEffect(() => {
    const onRemoteUpdate = (event) => {
      const update = event.detail;
      if (
        update.storyId === storyId &&
        update.actorId !== session.user?.id &&
        update.version > versionRef.current
      ) remoteSyncRef.current?.(update);
    };
    window.addEventListener("storyweave:story-updated", onRemoteUpdate);
    return () =>
      window.removeEventListener("storyweave:story-updated", onRemoteUpdate);
  }, [storyId, session.user?.id]);
  useEffect(() => {
    const onPublished = (event) => {
      const update = event.detail;
      if (update.storyId !== storyId) return;
      setPublished({ version: update.version, publishedAt: update.publishedAt, readerUrl: update.readerUrl });
      if (update.actorId !== session.user?.id) setSyncMessage(`${update.actorName || "Another editor"} published version ${update.version}.`);
    };
    window.addEventListener("storyweave:story-published", onPublished);
    return () => window.removeEventListener("storyweave:story-published", onPublished);
  }, [storyId, session.user?.id]);
  useEffect(() => {
    if (!storyId || storyId === "demo") return;
    const refreshSharedState = () => api(`/stories/${storyId}/publish-status`).then((data) => {
      setPublished(data.published);
      if (data.draftVersion > versionRef.current) remoteSyncRef.current?.({ version: data.draftVersion, actorName: "another editor" });
    }).catch(() => {});
    const onVisibility = () => { if (globalThis.document.visibilityState === "visible") refreshSharedState(); };
    const fallback = setInterval(() => { if (globalThis.document.visibilityState === "visible") refreshSharedState(); }, 2000);
    window.addEventListener("focus", refreshSharedState);
    globalThis.document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(fallback);
      window.removeEventListener("focus", refreshSharedState);
      globalThis.document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [storyId]);
  useEffect(() => {
    const onPresence = (event) => {
      if (event.detail.storyId === storyId) setPresence(event.detail.presence || []);
    };
    window.addEventListener("storyweave:story-presence", onPresence);
    return () => window.removeEventListener("storyweave:story-presence", onPresence);
  }, [storyId]);
  const selected =
    document.passages.find((passage) => passage.id === selectedId) ||
    document.passages[0];
  useEffect(() => {
    if (!storyId || storyId === "demo") return;
    let active = true;
    const heartbeat = () => api(`/stories/${storyId}/presence`, {
      method: "POST",
      body: JSON.stringify({ passageId: selectedId || null, panel }),
    }).then((data) => { if (active) setPresence(data.presence || []); }).catch(() => {});
    heartbeat();
    const timer = setInterval(heartbeat, 15000);
    return () => { active = false; clearInterval(timer); };
  }, [storyId, selectedId, panel]);
  useEffect(() => () => {
    if (storyId && storyId !== "demo") fetch(`/api/stories/${storyId}/presence`, { method: "DELETE", credentials: "same-origin", keepalive: true }).catch(() => {});
  }, [storyId]);
  const saveDocument = async (next) => {
    if (storyId === "demo") { setSaved(true); return { version: versionRef.current }; }
    const result = await api(`/stories/${storyId}`, {
      method: "PUT",
      body: JSON.stringify({ title: story?.title, expectedVersion: versionRef.current, document: next }),
    });
    versionRef.current = result.version;
    baseDocumentRef.current = structuredClone(next);
    setStory((current) => current ? { ...current, version: result.version } : current);
    if (sameValue(documentRef.current, next)) { setSaved(true); savedRef.current = true; }
    return result;
  };
  const syncRemoteDocument = async (update = {}) => {
    if (syncInFlight.current || (update.version && update.version <= versionRef.current)) return;
    syncInFlight.current = true;
    try {
      const data = await api(`/stories/${storyId}`);
      if (data.story.version <= versionRef.current) return;
      const remoteDocument = data.story.document, localDocument = documentRef.current, wasSaved = savedRef.current;
      versionRef.current = data.story.version;
      setStory(data.story);
      setPublished(data.story.published);
      setRemoteVersion(null);
      if (wasSaved) {
        baseDocumentRef.current = structuredClone(remoteDocument);
        documentRef.current = remoteDocument;
        setDocument(remoteDocument);
        setSaved(true);
        setSyncMessage(`Live update applied from ${update.actorName || "another editor"}.`);
      } else {
        const merged = mergeStoryDocument(baseDocumentRef.current, localDocument, remoteDocument);
        baseDocumentRef.current = structuredClone(remoteDocument);
        documentRef.current = merged.document;
        setDocument(merged.document);
        if (merged.conflicts.length) {
          clearTimeout(saveTimer.current);
          setMergeConflict({ conflicts: merged.conflicts, remoteDocument, mergedDocument: merged.document });
          setRemoteVersion(data.story.version);
        } else {
          setSyncMessage("Remote passages merged with your unsaved work.");
          clearTimeout(saveTimer.current);
          saveTimer.current = setTimeout(() => saveDocument(merged.document).catch((cause) => { if (cause.status === 409) remoteSyncRef.current?.({ version: cause.data?.actualVersion }); }), 500);
        }
      }
      if (!remoteDocument.passages.some((item) => item.id === selectedId) && remoteDocument.passages[0]) setSelectedId(remoteDocument.passages[0].id);
    } catch (cause) {
      setRemoteVersion(update.version || true);
      setSyncMessage(`Live update could not be applied: ${cause.message}`);
    } finally { syncInFlight.current = false; }
  };
  remoteSyncRef.current = syncRemoteDocument;
  const updateDocument = (next, record = true) => {
    if (record) {
      undoRef.current = [...undoRef.current.slice(-49), structuredClone(document)];
      redoRef.current = [];
    }
    setDocument(next);
    documentRef.current = next;
    setSaved(false);
    savedRef.current = false;
    clearTimeout(saveTimer.current);
    if (storyId !== "demo" && !mergeConflict)
      saveTimer.current = setTimeout(async () => {
        try {
          await saveDocument(next);
        } catch (cause) {
          if (cause.status === 409) remoteSyncRef.current?.({ version: cause.data?.actualVersion });
        }
      }, 350);
    else saveTimer.current = setTimeout(() => setSaved(true), 350);
  };
  const undo = () => {
    const previous = undoRef.current.pop();
    if (!previous) return;
    redoRef.current.push(structuredClone(document));
    updateDocument(previous, false);
  };
  const redo = () => {
    const next = redoRef.current.pop();
    if (!next) return;
    undoRef.current.push(structuredClone(document));
    updateDocument(next, false);
  };
  const startPreview = () => { setTestId(document.passages[0]?.id); setTesting(true); };
  const publishStory = async () => {
    try {
      clearTimeout(saveTimer.current);
      if (!saved) await saveDocument(document);
      if (storyId === "demo") return setPublished({ version: 1, publishedAt: new Date().toISOString(), readerUrl: "/read/demo" });
      const result = await api(`/stories/${storyId}/publish`, { method: "POST" });
      setPublished(result);
    } catch (cause) { setPublished({ error: cause.message }); }
  };
  const keepMergedChanges = async () => {
    if (!mergeConflict) return;
    try {
      setMergeConflict(null);
      setRemoteVersion(null);
      await saveDocument(mergeConflict.mergedDocument);
      setSyncMessage("Your conflicting passage version was kept and saved.");
    } catch (cause) { setSyncMessage(`Conflict save failed: ${cause.message}`); }
  };
  const useRemoteChanges = () => {
    if (!mergeConflict) return;
    clearTimeout(saveTimer.current);
    baseDocumentRef.current = structuredClone(mergeConflict.remoteDocument);
    documentRef.current = mergeConflict.remoteDocument;
    setDocument(mergeConflict.remoteDocument);
    setMergeConflict(null);
    setRemoteVersion(null);
    setSaved(true);
    savedRef.current = true;
    setSyncMessage("Loaded the other editor’s version.");
  };
  const updatePassage = (patch) =>
    updateDocument({
      ...document,
      passages: document.passages.map((passage) =>
        passage.id === selectedId ? { ...passage, ...patch } : passage,
      ),
    });
  const addPassage = (position = {}) => {
    const newPassage = {
      id: crypto.randomUUID(),
      title: "Untitled passage",
      body: "Start writing…",
      x: Number.isFinite(position.x) ? Math.round(position.x) : 480,
      y: Number.isFinite(position.y) ? Math.round(position.y) : 250,
      links: [],
      tags: [],
    };
    updateDocument({
      ...document,
      passages: [...document.passages, newPassage],
    });
    setSelectedId(newPassage.id);
    setPanel("write");
  };
  const openContextMenu = (event, passageId = null, canvasPosition = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (passageId) setSelectedId(passageId);
    const width = passageId ? 230 : 210, height = passageId ? 250 : 110;
    setContextMenu({
      passageId,
      canvasPosition,
      x: Math.max(8, Math.min(event.clientX, window.innerWidth - width - 8)),
      y: Math.max(8, Math.min(event.clientY, window.innerHeight - height - 8)),
    });
  };
  const duplicateSelectedPassage = (passageId) => {
    const result = duplicatePassage(document, passageId, crypto.randomUUID());
    if (!result.passage) return;
    updateDocument(result.document);
    setSelectedId(result.passage.id);
    setPanel("write");
    setContextMenu(null);
  };
  const setStartingPassage = (passageId) => {
    const next = makeStartingPassage(document, passageId);
    if (next !== document) updateDocument(next);
    setSelectedId(passageId);
    setContextMenu(null);
    setSyncMessage("Starting passage updated.");
  };
  const confirmPassageDeletion = () => {
    if (!deleteTarget) return;
    try {
      const deletedIndex = document.passages.findIndex((passage) => passage.id === deleteTarget.id);
      const next = deletePassage(document, deleteTarget.id);
      const nextSelected = next.passages[Math.min(Math.max(deletedIndex, 0), next.passages.length - 1)]?.id;
      updateDocument(next);
      setSelectedId(nextSelected);
      setDeleteTarget(null);
      setContextMenu(null);
      setSyncMessage(`Deleted “${deleteTarget.title}” and removed incoming choices.`);
    } catch (cause) {
      setDeleteTarget(null);
      setContextMenu(null);
      setSyncMessage(cause.message);
    }
  };
  const beginVisualLink = (event, passageId) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedId(passageId);
    setLinkSourceId((current) => current === passageId ? null : passageId);
  };
  const completeVisualLink = (targetId) => {
    if (!linkSourceId || targetId === linkSourceId) return;
    const source = document.passages.find((passage) => passage.id === linkSourceId);
    const target = document.passages.find((passage) => passage.id === targetId);
    if (!source || !target) return setLinkSourceId(null);
    if ((source.links || []).some((link) => link.target === targetId)) {
      setSelectedId(source.id);
      setPanel("logic");
      setLinkSourceId(null);
      return setSyncMessage(`“${source.title}” already links to “${target.title}”.`);
    }
    updateDocument({
      ...document,
      passages: document.passages.map((passage) => passage.id === source.id ? {
        ...passage,
        links: [...(passage.links || []), { label: `Go to ${target.title}`, target: target.id, condition: null, effect: null }],
      } : passage),
    });
    setSelectedId(source.id);
    setPanel("logic");
    setLinkSourceId(null);
    setSyncMessage(`Linked “${source.title}” to “${target.title}”. Edit the choice label in Logic.`);
  };
  const passageEditors = (passageId) => presence.filter((editor) => editor.passageId === passageId);
  const activeOthers = presence.filter((editor) => editor.userId !== session.user?.id);
  const editorLocation = (editor) => {
    const passage = document.passages.find((item) => item.id === editor.passageId);
    return passage ? `${editor.panel} · ${passage.title}` : editor.panel || "Story map";
  };
  return (
    <div className="editor-page">
      <header className="editor-top">
        <Link to="/workspaces">
          <ArrowLeft />
        </Link>
        <div>
          <strong>{story?.title || "The Last Broadcast"}</strong>
          <small>
            <Cloud /> {saved ? "All changes saved" : "Saving…"}
          </small>
        </div>
        <div className="editor-top-actions">
          {mergeConflict ? <div className="merge-alert"><span><Activity /><strong>Editing conflict</strong><small>{mergeConflict.conflicts.length} overlapping change{mergeConflict.conflicts.length === 1 ? "" : "s"}</small></span><Button variant="ghost" onClick={useRemoteChanges}>Use theirs</Button><Button onClick={keepMergedChanges}>Keep mine</Button></div> : remoteVersion && (
            <Button className="remote-update" variant="accent" onClick={() => remoteSyncRef.current?.({ version: remoteVersion })}>
              <Activity /> Sync remote update
            </Button>
          )}
          <div className="presence-wrap">
            <button className="presence-trigger" onClick={() => setPresenceOpen(!presenceOpen)} aria-expanded={presenceOpen}>
              <span className="presence-stack">{presence.slice(0, 4).map((editor) => <Avatar key={editor.sessionId} name={editor.displayName} image={editor.avatarUrl} />)}</span>
              <span><strong>{presence.length || 1} editing</strong><small>{activeOthers.length ? `${activeOthers.length} collaborator${activeOthers.length === 1 ? "" : "s"} live` : "Only you right now"}</small></span>
              <ChevronDown />
            </button>
            {presenceOpen && <div className="presence-menu">{presence.map((editor) => <div key={editor.sessionId}><Avatar name={editor.displayName} image={editor.avatarUrl} /><span><strong>{editor.displayName}{editor.userId === session.user?.id ? " (you)" : ""}</strong><small><span className="live-dot" /> {editorLocation(editor)}</small></span></div>)}</div>}
          </div>
          <Button variant="ghost" onClick={() => setAccessOpen(true)}>
            <LockKeyhole /> Access
          </Button>
          <Button variant="ghost" onClick={startPreview}>
            <Eye /> Preview
          </Button>
          <button className={`published-status ${published?.version ? "live" : "unpublished"}`} onClick={() => published?.readerUrl && window.open(published.readerUrl, "_blank")} disabled={!published?.version} title={published?.publishedAt ? `Published ${new Date(published.publishedAt).toLocaleString()}` : "This story has not been published"}>
            <span><span className="live-dot" /> {published?.version ? "Published" : "Not published"}</span>
            <strong>{published?.version ? `v${published.version}` : "Draft only"}</strong>
          </button>
          <Button onClick={publishStory}>
            <Rocket /> Publish changes
          </Button>
          {published?.version && <Button variant="ghost" onClick={() => window.open(published.readerUrl || `/read/${storyId}`, "_blank")}><Eye /> Open reader</Button>}
        </div>
      </header>
      <div className="editor-workbench">
        <aside className="passage-sidebar">
          <div className="mini-heading">
            <span>PASSAGES · {document.passages.length}</span>
            <Button variant="icon" onClick={addPassage}>
              <Plus />
            </Button>
          </div>
          <div className="search-field">
            <Search />
            <input value={passageSearch} onChange={(e) => setPassageSearch(e.target.value)} placeholder="Find a passage…" />
          </div>
          {document.passages.filter((passage) => passage.title.toLowerCase().includes(passageSearch.toLowerCase())).map((passage) => {
            const index = document.passages.indexOf(passage);
            return (
            <button
              className={selectedId === passage.id ? "active" : ""}
              onClick={() => setSelectedId(passage.id)}
              onContextMenu={(event) => openContextMenu(event, passage.id)}
              key={passage.id}
            >
              <span
                className={
                  index === 0
                    ? "node start"
                    : !passage.links.length
                      ? "node end"
                      : "node"
                }
              />
              <span className="passage-list-title">{passage.title}</span>
              {passageEditors(passage.id).length ? <span className="passage-presence">{passageEditors(passage.id).slice(0, 3).map((editor) => <Avatar key={editor.sessionId} name={editor.displayName} image={editor.avatarUrl} />)}</span> : <small>{passage.links.length}</small>}
            </button>
          );})}
          <section className="story-state-panel">
            <header><span><Database /> Story state</span><Button variant="icon" aria-label="Manage variables" onClick={() => setPanel("variables")}><Settings /></Button></header>
            <div>{document.variables.map((variable) => <button key={variable.id} onClick={() => setPanel("variables")}><span className={`variable-type ${variable.type}`}><Braces /></span><span><strong>{variable.name}</strong><small>{variable.type} · {Array.isArray(variable.defaultValue) ? variable.defaultValue.join(", ") || "empty" : String(variable.defaultValue)}</small></span></button>)}{!document.variables.length && <button className="empty-variable" onClick={() => setPanel("variables")}><Plus /> Create your first variable</button>}</div>
          </section>
        </aside>
        <section className="map-area">
          <div className="map-toolbar">
            <div>
              <Button variant="icon" onClick={undo} disabled={!undoRef.current.length}>
                <Undo2 />
              </Button>
              <Button variant="icon" onClick={redo} disabled={!redoRef.current.length}>
                <Redo2 />
              </Button>
              <Button variant="icon" onClick={addPassage}>
                <Plus />
              </Button>
              <Button variant={linkSourceId ? "accent" : "icon"} className="visual-link-toolbar" title={linkSourceId ? "Cancel linking" : "Link selected passage to another card"} onClick={() => selected && setLinkSourceId((current) => current ? null : selected.id)}>
                <Link2 />
              </Button>
            </div>
            <span>
              <Workflow /> Story map
            </span>
            <div>
              <Button
                variant="icon"
                onClick={() => setZoom(Math.max(0.55, zoom - 0.08))}
              >
                <ZoomOut />
              </Button>
              <button onClick={() => { setZoom(1); setPan({ x: 20, y: 20 }); }}>{Math.round(zoom * 100)}%</button>
              <Button
                variant="icon"
                onClick={() => setZoom(Math.min(1.2, zoom + 0.08))}
              >
                <ZoomIn />
              </Button>
              <Button
                onClick={startPreview}
              >
                <Play /> Test
              </Button>
            </div>
          </div>
          <div className="graph-viewport"
            onContextMenu={(event) => {
              if (event.target.closest?.(".graph-node")) return;
              const rect = event.currentTarget.getBoundingClientRect();
              openContextMenu(event, null, {
                x: (event.clientX - rect.left - pan.x) / zoom,
                y: (event.clientY - rect.top - pan.y) / zoom,
              });
            }}
            onWheel={(e) => {
              e.preventDefault();
              const rect = e.currentTarget.getBoundingClientRect(), point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
              const next = Math.max(0.4, Math.min(1.6, zoom * (e.deltaY > 0 ? 0.9 : 1.1)));
              const ratio = next / zoom;
              setPan((current) => ({ x: point.x - (point.x - current.x) * ratio, y: point.y - (point.y - current.y) * ratio }));
              setZoom(next);
            }}
            onPointerDown={(e) => { if (e.button === 0 && !e.target.closest?.(".graph-node")) { panDrag.current = { x: e.clientX, y: e.clientY, pan }; e.currentTarget.setPointerCapture(e.pointerId); } }}
            onPointerMove={(e) => { if (panDrag.current) setPan({ x: panDrag.current.pan.x + e.clientX - panDrag.current.x, y: panDrag.current.pan.y + e.clientY - panDrag.current.y }); }}
            onPointerUp={() => { panDrag.current = null; }}
          >
            <div className="graph" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}>
              <svg width="1100" height="650">
                {document.passages.flatMap((source) =>
                  source.links.map((link) => {
                    const target = document.passages.find(
                      (passage) => passage.id === link.target,
                    );
                    if (!target) return null;
                    return (
                      <path
                        key={source.id + link.target}
                        d={`M${source.x + 220} ${source.y + 55} C${source.x + 290} ${source.y + 55}, ${target.x - 70} ${target.y + 55}, ${target.x} ${target.y + 55}`}
                      />
                    );
                  }),
                )}
              </svg>
              {document.passages.map((passage, index) => (
                <div
                  key={passage.id}
                  role="button"
                  tabIndex="0"
                  aria-label={`${passage.title}${linkSourceId && linkSourceId !== passage.id ? "; click to link here" : ""}`}
                  className={`graph-node ${selectedId === passage.id ? "selected" : ""} ${index === 0 ? "start" : !passage.links.length ? "end" : ""} ${linkSourceId === passage.id ? "linking-source" : linkSourceId ? "linking-target" : ""}`}
                  style={{ left: passage.x, top: passage.y }}
                  onClick={() => linkSourceId && linkSourceId !== passage.id ? completeVisualLink(passage.id) : setSelectedId(passage.id)}
                  onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); linkSourceId && linkSourceId !== passage.id ? completeVisualLink(passage.id) : setSelectedId(passage.id); } }}
                  onContextMenu={(event) => openContextMenu(event, passage.id)}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    e.stopPropagation();
                    if (linkSourceId) return;
                    nodeDrag.current = { id: passage.id, x: e.clientX, y: e.clientY, startX: passage.x, startY: passage.y };
                    undoRef.current = [...undoRef.current.slice(-49), structuredClone(document)]; redoRef.current = [];
                    e.currentTarget.setPointerCapture(e.pointerId);
                  }}
                  onPointerMove={(e) => {
                    if (nodeDrag.current?.id !== passage.id) return;
                    const nextX = Math.round(nodeDrag.current.startX + (e.clientX - nodeDrag.current.x) / zoom);
                    const nextY = Math.round(nodeDrag.current.startY + (e.clientY - nodeDrag.current.y) / zoom);
                    updateDocument({ ...document, passages: document.passages.map((item) => item.id === passage.id ? { ...item, x: nextX, y: nextY } : item) }, false);
                  }}
                  onPointerUp={() => { nodeDrag.current = null; setSelectedId(passage.id); }}
                >
                  <header>
                    <span>
                      {index === 0 ? (
                        <Flag />
                      ) : !passage.links.length ? (
                        <Check />
                      ) : (
                        <FileText />
                      )}
                    </span>
                    <strong>{passage.title}</strong>
                    {passageEditors(passage.id).length > 0 && <span className="node-presence">{passageEditors(passage.id).slice(0, 2).map((editor) => <Avatar key={editor.sessionId} name={editor.displayName} image={editor.avatarUrl} />)}</span>}
                  </header>
                  <p>{passage.body}</p>
                  <footer>
                    <span>
                      {passage.tags?.map((tag) => (
                        <Badge key={tag}>{tag}</Badge>
                      ))}
                    </span>
                    <small>
                      <GitBranch /> {passage.links.length}
                    </small>
                  </footer>
                  <button className="node-connector" aria-label={linkSourceId === passage.id ? `Cancel link from ${passage.title}` : `Start link from ${passage.title}`} title={linkSourceId === passage.id ? "Cancel linking" : "Drag-free link: click, then choose another card"} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => beginVisualLink(event, passage.id)}><Link2 /></button>
                </div>
              ))}
            </div>
            {linkSourceId && <div className="link-mode-banner"><Link2 /><span><strong>Linking from {document.passages.find((passage) => passage.id === linkSourceId)?.title}</strong><small>Click another passage card to create a choice.</small></span><button onClick={() => setLinkSourceId(null)}>Cancel</button></div>}
            <div className="graph-status">
              <Activity /> Live · {presence.length || 1} editor{(presence.length || 1) === 1 ? "" : "s"}
            </div>
          </div>
        </section>
        <aside className="inspector">
          <header>
            <span>
              <FileText />
            </span>
            <div>
              <small>EDITING PASSAGE</small>
              <strong>{selected?.title}</strong>
            </div>
            <span className="inspector-presence">{passageEditors(selected?.id).filter((editor) => editor.userId !== session.user?.id).slice(0, 3).map((editor) => <Avatar key={editor.sessionId} name={editor.displayName} image={editor.avatarUrl} />)}</span>
            {selected && <Button variant="icon" aria-label="Passage actions" title="Passage actions" onClick={(event) => openContextMenu(event, selected.id)}><MoreHorizontal /></Button>}
          </header>
          <nav>
            {[
              ["write", FileText, "Write"],
              ["logic", Braces, "Logic"],
              ["design", Palette, "Design"],
              ["variables", Database, "Variables"],
            ].map(([key, Icon, label]) => (
              <button
                className={panel === key ? "active" : ""}
                onClick={() => setPanel(key)}
                key={key}
              >
                <Icon />
                {label}
              </button>
            ))}
          </nav>
          {panel === "write" && (
            <WritePanel passage={selected} update={updatePassage} />
          )}
          {panel === "logic" && (
            <LogicPanel
              passage={selected}
              document={document}
              update={updatePassage}
            />
          )}
          {panel === "design" && (
            <DesignPanel document={document} update={updateDocument} />
          )}
          {panel === "variables" && (
            <VariablesPanel document={document} update={updateDocument} />
          )}
        </aside>
      </div>
      {contextMenu && <div className="context-menu" role="menu" aria-label={contextMenu.passageId ? "Passage actions" : "Story map actions"} style={{ left: contextMenu.x, top: contextMenu.y }} onPointerDown={(event) => event.stopPropagation()}>
        {contextMenu.passageId ? <>
          <button role="menuitem" onClick={() => { setSelectedId(contextMenu.passageId); setPanel("write"); setContextMenu(null); }}><FileText /><span><strong>Edit passage</strong><small>Open writing tools</small></span></button>
          <button role="menuitem" onClick={() => { setSelectedId(contextMenu.passageId); setPanel("logic"); setContextMenu(null); }}><GitBranch /><span><strong>Edit choices</strong><small>Connections and conditions</small></span></button>
          <button role="menuitem" onClick={() => duplicateSelectedPassage(contextMenu.passageId)}><Copy /><span><strong>Duplicate</strong><small>Copy passage and choices</small></span></button>
          <button role="menuitem" disabled={document.passages[0]?.id === contextMenu.passageId} onClick={() => setStartingPassage(contextMenu.passageId)}><Flag /><span><strong>Make starting passage</strong><small>Readers begin here</small></span></button>
          <div className="context-separator" />
          <button role="menuitem" className="danger" disabled={document.passages.length <= 1} onClick={() => { setDeleteTarget(document.passages.find((passage) => passage.id === contextMenu.passageId)); setContextMenu(null); }}><Trash2 /><span><strong>Delete passage</strong><small>{document.passages.length <= 1 ? "A story needs one passage" : "Also removes incoming choices"}</small></span></button>
        </> : <>
          <button role="menuitem" onClick={() => { addPassage(contextMenu.canvasPosition); setContextMenu(null); }}><Plus /><span><strong>New passage here</strong><small>Add at this map position</small></span></button>
          <button role="menuitem" onClick={() => { setZoom(1); setPan({ x: 20, y: 20 }); setContextMenu(null); }}><Workflow /><span><strong>Reset map view</strong><small>Return to 100%</small></span></button>
        </>}
      </div>}
      {deleteTarget && <div className="modal-backdrop" onMouseDown={() => setDeleteTarget(null)}>
        <section className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-passage-title" onMouseDown={(event) => event.stopPropagation()}>
          <span className="confirm-icon"><Trash2 /></span>
          <div><h2 id="delete-passage-title">Delete “{deleteTarget.title}”?</h2><p>The passage and every choice that points to it will be removed. You can use Undo until you leave or reload the editor.</p></div>
          <footer><Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button><Button className="danger-button" onClick={confirmPassageDeletion}><Trash2 /> Delete passage</Button></footer>
        </section>
      </div>}
      {testing && (
        <TestPlayer
          document={document}
          storyTitle={story?.title || "Story preview"}
          passageId={testId}
          setPassageId={setTestId}
          close={() => setTesting(false)}
        />
      )}
      {published?.error && <div className="error-banner editor-notice">Publish failed: {published.error}</div>}
      {syncMessage && <button className="sync-toast" onClick={() => setSyncMessage("")}><Activity /><span>{syncMessage}</span><X /></button>}
      {accessOpen && (
        <AccessPanel storyId={storyId} close={() => setAccessOpen(false)} />
      )}
    </div>
  );
}

function WritePanel({ passage, update }) {
  const editorRef = useRef();
  if (!passage) return null;
  const wrapSelection = (before, after = before) => {
    const input = editorRef.current;
    const start = input.selectionStart, end = input.selectionEnd;
    const selected = passage.body.slice(start, end) || "text";
    update({ body: passage.body.slice(0, start) + before + selected + after + passage.body.slice(end) });
    requestAnimationFrame(() => { input.focus(); input.setSelectionRange(start + before.length, start + before.length + selected.length); });
  };
  const addTag = () => {
    const tag = prompt("Tag name");
    if (tag?.trim() && !passage.tags?.includes(tag.trim())) update({ tags: [...(passage.tags || []), tag.trim()] });
  };
  const insertText = (text) => {
    const input = editorRef.current, start = input.selectionStart, end = input.selectionEnd;
    update({ body: passage.body.slice(0, start) + text + passage.body.slice(end) });
    requestAnimationFrame(() => { input.focus(); input.setSelectionRange(start + text.length, start + text.length); });
  };
  return (
    <div className="inspector-body">
      <label>
        PASSAGE NAME
        <input
          value={passage.title}
          onChange={(e) => update({ title: e.target.value })}
        />
      </label>
      <label>
        CONTENT <span>{passage.body.length} characters</span>
        <div className="format-bar">
          <button type="button" title="Bold" aria-label="Bold" onClick={() => wrapSelection("**")}>
            <b>B</b>
          </button>
          <button type="button" title="Italic" aria-label="Italic" onClick={() => wrapSelection("*")}>
            <i>I</i>
          </button>
          <button type="button" title="Link" aria-label="Link" onClick={() => wrapSelection("[", "](https://)")}>
            <Link2 />
          </button>
          <button type="button" title="Inline code" aria-label="Inline code" onClick={() => wrapSelection("`", "`")}>
            <Code2 />
          </button>
          <button type="button" title="Heading" aria-label="Heading" onClick={() => wrapSelection("## ", "")}><b>H</b></button>
          <button type="button" title="Bulleted list" aria-label="Bulleted list" onClick={() => wrapSelection("- ", "")}><span>•</span></button>
          <button type="button" title="Blockquote" aria-label="Blockquote" onClick={() => wrapSelection("> ", "")}><span>❯</span></button>
          <button type="button" title="Insert table" aria-label="Insert table" onClick={() => insertText("\n| Column 1 | Column 2 |\n| --- | --- |\n| Value | Value |\n")}><Database /></button>
        </div>
        <textarea
          ref={editorRef}
          value={passage.body}
          onChange={(e) => update({ body: e.target.value })}
        />
      </label>
      <details className="markdown-preview">
        <summary><Eye /> Preview Markdown</summary>
        <Markdown content={passage.body} />
      </details>
      <div className="smart-tip">
        <Sparkles />
        <span>
          <strong>Markdown + visual links</strong> Passage text supports headings, emphasis, links, lists, quotes, code, and tables. Use a card’s connector handle to create story choices without memorizing syntax.
        </span>
      </div>
      <label>
        TAGS
        <div className="tag-field">
          {passage.tags?.map((tag) => (
            <Badge key={tag}>{tag}</Badge>
          ))}
          <button type="button" onClick={addTag}>
            <Plus /> Add tag
          </button>
        </div>
      </label>
    </div>
  );
}
function LogicPanel({ passage, document, update }) {
  const replaceLink = (index, patch) => update({
    links: passage.links.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
  });
  return (
    <div className="inspector-body">
      <div className="panel-intro">
        <Braces />
        <h3>Choices & conditions</h3>
        <p>Send readers to another passage when a condition is true.</p>
      </div>
      {passage.links.map((link, index) => (
        <div className="logic-card" key={index}>
          <label>
            Choice label
            <input
              value={link.label}
              onChange={(e) =>
                update({
                  links: passage.links.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, label: e.target.value }
                      : item,
                  ),
                })
              }
            />
          </label>
          <label>
            Target
            <select
              value={link.target}
              onChange={(e) =>
                update({
                  links: passage.links.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, target: e.target.value }
                      : item,
                  ),
                })
              }
            >
              {document.passages
                .filter((item) => item.id !== passage.id)
                .map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.title}
                  </option>
                ))}
            </select>
          </label>
          <div className="condition-builder">
            <label>
              Availability
              <select value={conditionRule(link, document.variables).mode} onChange={(e) => replaceLink(index, {
                condition: "",
                conditionRule: e.target.value === "always"
                  ? { mode: "always", variableId: "", operator: "", value: "" }
                  : { mode: "when", variableId: document.variables[0]?.id || "", operator: operatorOptions[document.variables[0]?.type]?.[0]?.[0] || "eq", value: "" },
              })}>
                <option value="always">Always available</option>
                <option value="when" disabled={!document.variables.length}>Only when a variable matches</option>
              </select>
            </label>
            {conditionRule(link, document.variables).mode === "when" && (() => {
              const rule = conditionRule(link, document.variables);
              const variable = document.variables.find((item) => item.id === rule.variableId) || document.variables[0];
              const operators = operatorOptions[variable?.type] || operatorOptions.string;
              return <div className="condition-row">
                <label>Variable<select value={variable?.id || ""} onChange={(e) => {
                  const nextVariable = document.variables.find((item) => item.id === e.target.value);
                  replaceLink(index, { condition: "", conditionRule: { ...rule, variableId: e.target.value, operator: operatorOptions[nextVariable?.type]?.[0]?.[0] || "eq", value: "" } });
                }}>{document.variables.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
                <label>Rule<select value={rule.operator} onChange={(e) => replaceLink(index, { condition: "", conditionRule: { ...rule, operator: e.target.value } })}>{operators.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                {!['is_true', 'is_false'].includes(rule.operator) && <label>Value{variable?.type === "number" ? <input type="number" value={rule.value} onChange={(e) => replaceLink(index, { condition: "", conditionRule: { ...rule, value: e.target.value } })} /> : <input value={rule.value} onChange={(e) => replaceLink(index, { condition: "", conditionRule: { ...rule, value: e.target.value } })} />}</label>}
              </div>;
            })()}
          </div>
          <div className="condition-builder">
            <label>When selected<select value={link.effectRule?.variableId ? "change" : "none"} onChange={(e) => {
              const variable = document.variables[0];
              replaceLink(index, { effectRule: e.target.value === "none" || !variable ? null : { variableId: variable.id, operation: effectOptions[variable.type][0][0], value: "" } });
            }}><option value="none">Do not change a variable</option><option value="change" disabled={!document.variables.length}>Change a variable</option></select></label>
            {link.effectRule?.variableId && (() => {
              const effect = link.effectRule, variable = document.variables.find((item) => item.id === effect.variableId) || document.variables[0];
              const operations = effectOptions[variable?.type] || effectOptions.string;
              return <div className="condition-row">
                <label>Variable<select value={variable?.id || ""} onChange={(e) => { const nextVariable = document.variables.find((item) => item.id === e.target.value); replaceLink(index, { effectRule: { variableId: e.target.value, operation: effectOptions[nextVariable.type][0][0], value: "" } }); }}>{document.variables.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
                <label>Action<select value={effect.operation} onChange={(e) => replaceLink(index, { effectRule: { ...effect, operation: e.target.value } })}>{operations.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                {!['set_true', 'set_false', 'toggle'].includes(effect.operation) && <label>Value<input type={variable?.type === "number" ? "number" : "text"} value={effect.value || ""} onChange={(e) => replaceLink(index, { effectRule: { ...effect, value: e.target.value } })} /></label>}
              </div>;
            })()}
          </div>
          <Button variant="icon" aria-label="Delete choice" onClick={() => update({ links: passage.links.filter((_, itemIndex) => itemIndex !== index) })}><Trash2 /></Button>
        </div>
      ))}
      <Button
        variant="ghost"
        onClick={() =>
          update({
            links: [
              ...passage.links,
              {
                label: "New choice",
                target:
                  document.passages.find((item) => item.id !== passage.id)
                    ?.id || "",
                condition: "",
                conditionRule: { mode: "always", variableId: "", operator: "", value: "" },
              },
            ],
          })
        }
      >
        <Plus /> Add choice
      </Button>
    </div>
  );
}
function DesignPanel({ document, update }) {
  return (
    <div className="inspector-body">
      <div className="panel-intro">
        <Palette />
        <h3>Reader appearance</h3>
        <p>Choose how this story feels when published.</p>
      </div>
      <label>
        READER THEME
        <div className="theme-mini-grid">
          {presets.slice(0, 8).map((theme) => (
            <button
              className={
                document.styles?.readerTheme === theme.id ? "active" : ""
              }
              onClick={() =>
                update({
                  ...document,
                  styles: { ...document.styles, readerTheme: theme.id },
                })
              }
              key={theme.id}
            >
              <span style={{ background: theme.accent }} />
              {theme.name}
            </button>
          ))}
        </div>
      </label>
      <label>
        TRANSITION
        <select
          value={document.styles?.transition || "fade"}
          onChange={(e) =>
            update({
              ...document,
              styles: { ...document.styles, transition: e.target.value },
            })
          }
        >
          <option value="fade">Fade</option>
          <option value="slide">Slide</option>
          <option value="typewriter">Typewriter</option>
          <option value="none">None</option>
        </select>
      </label>
    </div>
  );
}
function VariablesPanel({ document, update }) {
  const add = () =>
    update({
      ...document,
      variables: [
        ...document.variables,
        {
          id: crypto.randomUUID(),
          name: "new_variable",
          type: "boolean",
          defaultValue: false,
          description: "",
        },
      ],
    });
  return (
    <div className="inspector-body">
      <div className="panel-intro">
        <Database />
        <h3>Story variables</h3>
        <p>Remember choices, track inventory, and control conditional paths.</p>
      </div>
      {document.variables.map((variable, index) => (
        <div className="variable-card" key={variable.id}>
          <div>
            <Braces />
            <input
              value={variable.name}
              onChange={(e) =>
                update({
                  ...document,
                  variables: document.variables.map((item, i) =>
                    i === index ? { ...item, name: e.target.value } : item,
                  ),
                })
              }
            />
            <Button variant="icon" aria-label={`Delete ${variable.name}`} onClick={() => update({
              ...document,
              variables: document.variables.filter((_, i) => i !== index),
            })}>
              <Trash2 />
            </Button>
          </div>
          <select
            value={variable.type}
            onChange={(e) =>
              update({
                ...document,
                variables: document.variables.map((item, i) =>
                  i === index ? { ...item, type: e.target.value, defaultValue: e.target.value === "boolean" ? false : e.target.value === "number" ? 0 : e.target.value === "list" ? [] : "" } : item,
                ),
              })
            }
          >
            <option>boolean</option>
            <option>number</option>
            <option>string</option>
            <option>list</option>
          </select>
          {variable.type === "boolean" ? <select value={String(variable.defaultValue)} onChange={(e) => update({
            ...document,
            variables: document.variables.map((item, i) => i === index ? { ...item, defaultValue: e.target.value === "true" } : item),
          })}><option value="false">false</option><option value="true">true</option></select> : <input
            type={variable.type === "number" ? "number" : "text"}
            value={Array.isArray(variable.defaultValue) ? variable.defaultValue.join(", ") : String(variable.defaultValue ?? "")}
            onChange={(e) =>
              update({
                ...document,
                variables: document.variables.map((item, i) =>
                  i === index
                    ? { ...item, defaultValue: variable.type === "number" ? Number(e.target.value) : variable.type === "list" ? e.target.value.split(",").map((value) => value.trim()).filter(Boolean) : e.target.value }
                    : item,
                ),
              })
            }
            placeholder="Default value"
          />}
        </div>
      ))}
      <Button onClick={add}>
        <Plus /> New variable
      </Button>
    </div>
  );
}

function TestPlayer({ document, passageId, setPassageId, close, storyTitle = "Story preview" }) {
  const initialValues = useMemo(() => Object.fromEntries(document.variables.map((variable) => [variable.id, variable.defaultValue])), [document.variables]);
  const [values, setValues] = useState(initialValues);
  const passage =
    document.passages.find((item) => item.id === passageId) ||
    document.passages[0];
  const availableLinks = passage.links.filter((link) => conditionMatches(link, document.variables, values));
  return (
    <div className="modal-backdrop">
      <div className="test-player">
        <header>
          <div>
            <Badge>
              <span className="live-dot" /> TEST MODE
            </Badge>
            <strong>{storyTitle}</strong>
          </div>
          <Button variant="icon" onClick={close}>
            <X />
          </Button>
        </header>
        <article>
          <small>PASSAGE</small>
          <h1>{passage.title}</h1>
          <Markdown content={passage.body} className="markdown reader-markdown" />
          <div className="test-choices">
            {availableLinks.map((link, index) => (
              <button
                key={`${link.target}-${index}`}
                onClick={() => { setValues(applyChoiceEffect(link, document.variables, values)); setPassageId(link.target); }}
              >
                <span>{index + 1}</span>
                {link.label}
                <ArrowLeft />
              </button>
            ))}
            {!availableLinks.length && (
              <Button onClick={() => { setValues(initialValues); setPassageId(document.passages[0].id); }}>
                <Redo2 /> Restart
              </Button>
            )}
          </div>
        </article>
        <footer>
          <Play /> Playing from the beginning{" "}
          <span>
            Variables <Badge>{document.variables.length}</Badge>
          </span>
        </footer>
      </div>
    </div>
  );
}

function PublishedReader() {
  const { storyId } = useParams();
  const [story, setStory] = useState(null), [passageId, setPassageId] = useState(null), [error, setError] = useState("");
  useEffect(() => { api(`/stories/${storyId}/published`).then((data) => { setStory(data.story); setPassageId(data.story.document.passages[0]?.id); }).catch((cause) => setError(cause.message)); }, [storyId]);
  if (error) return <div className="page"><div className="error-banner">Published story unavailable: {error}</div></div>;
  if (!story || !passageId) return <BootScreen />;
  return <TestPlayer document={story.document} storyTitle={story.title} passageId={passageId} setPassageId={setPassageId} close={() => history.back()} />;
}

function AccessPanel({ storyId, close }) {
  const [entries, setEntries] = useState([]);
  const [visibility, setVisibility] = useState("workspace");
  const [error, setError] = useState("");
  useEffect(() => {
    if (storyId === "demo") return;
    api(`/stories/${storyId}/access`).then((data) => {
      setVisibility(data.visibility);
      setEntries(data.entries.map((entry) => ({ subjectType: entry.subject_type, subjectValue: entry.subject_value, permission: entry.permission })));
    }).catch((cause) => setError(cause.message));
  }, [storyId]);
  const add = () =>
    setEntries([
      ...entries,
      { subjectType: "user", subjectValue: "", permission: "view" },
    ]);
  const save = async () => {
    try {
      if (storyId !== "demo") await api(`/stories/${storyId}/access`, {
          method: "PUT",
          body: JSON.stringify({ visibility, entries: entries.filter((entry) => entry.subjectValue || entry.subjectType === "everyone") }),
        });
      close();
    } catch (cause) { setError(cause.message); }
  };
  return (
    <div className="sheet-backdrop" onMouseDown={close}>
      <aside className="access-sheet" onMouseDown={(e) => e.stopPropagation()}>
        <header>
          <div>
            <span>
              <LockKeyhole />
            </span>
            <div>
              <h2>Story access</h2>
              <p>Control exactly who can open this story.</p>
            </div>
          </div>
          <Button variant="icon" onClick={close}>
            <X />
          </Button>
        </header>
        <div className="access-body">
          <label>
            BASE VISIBILITY
            <select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
              <option value="workspace">Workspace members</option>
              <option value="private">Private</option>
              <option value="tenant">Entire tenant</option>
            </select>
          </label>
          {error && <div className="error-banner">{error}</div>}
          <div className="access-note">
            <Shield />
            <span>
              <strong>Server-enforced authorization</strong> Story rules are
              checked after every token refresh and before content is decrypted.
            </span>
          </div>
          <div className="section-heading">
            <div>
              <h3>Access rules</h3>
              <p>
                OIDC groups, realm roles, client roles, system roles, or users
              </p>
            </div>
            <Button variant="ghost" onClick={add}>
              <Plus /> Add rule
            </Button>
          </div>
          {entries.map((entry, index) => (
            <div className="access-rule" key={index}>
              <select
                value={entry.subjectType}
                onChange={(e) =>
                  setEntries(
                    entries.map((item, i) =>
                      i === index
                        ? { ...item, subjectType: e.target.value }
                        : item,
                    ),
                  )
                }
              >
                <option value="group">OIDC group</option>
                <option value="realm_role">Realm role</option>
                <option value="client_role">Client role</option>
                <option value="system_role">System role</option>
                <option value="user">User</option>
              </select>
              <input
                placeholder="/team/writers"
                value={entry.subjectValue}
                onChange={(e) =>
                  setEntries(
                    entries.map((item, i) =>
                      i === index
                        ? { ...item, subjectValue: e.target.value }
                        : item,
                    ),
                  )
                }
              />
              <select
                value={entry.permission}
                onChange={(e) =>
                  setEntries(
                    entries.map((item, i) =>
                      i === index
                        ? { ...item, permission: e.target.value }
                        : item,
                    ),
                  )
                }
              >
                <option>view</option>
                <option>comment</option>
                <option>edit</option>
                <option>publish</option>
                <option>admin</option>
              </select>
              <Button
                variant="icon"
                onClick={() =>
                  setEntries(entries.filter((_, i) => i !== index))
                }
              >
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
        <footer>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button onClick={save}>
            <Save /> Save access
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function WorkspaceAccessPanel({ workspaceId, close, onSaved }) {
  const [entries, setEntries] = useState([]), [visibility, setVisibility] = useState("private"), [defaultPermission, setDefaultPermission] = useState("none"), [error, setError] = useState("");
  useEffect(() => { api(`/workspaces/${workspaceId}/access`).then((data) => {
    setVisibility(data.visibility); setDefaultPermission(data.defaultPermission);
    setEntries(data.entries.map((entry) => ({ subjectType: entry.subject_type, subjectValue: entry.subject_value, permission: entry.permission })));
  }).catch((cause) => setError(cause.message)); }, [workspaceId]);
  const save = async () => { try {
    await api(`/workspaces/${workspaceId}/access`, { method: "PUT", body: JSON.stringify({ visibility, defaultPermission, entries }) });
    onSaved?.(); close();
  } catch (cause) { setError(cause.message); } };
  return <div className="sheet-backdrop" onMouseDown={close}><aside className="access-sheet" onMouseDown={(e) => e.stopPropagation()}>
    <header><div><span><Users /></span><div><h2>Workspace access</h2><p>Control membership with OIDC identities and system roles.</p></div></div><Button variant="icon" onClick={close}><X /></Button></header>
    <div className="access-body">
      <label>VISIBILITY<select value={visibility} onChange={(e) => setVisibility(e.target.value)}><option value="private">Private</option><option value="tenant">Entire tenant</option></select></label>
      <label>DEFAULT PERMISSION<select value={defaultPermission} onChange={(e) => setDefaultPermission(e.target.value)}><option value="none">None</option><option value="view">View</option><option value="comment">Comment</option><option value="edit">Edit</option></select></label>
      {error && <div className="error-banner">{error}</div>}
      <div className="section-heading"><div><h3>Access rules</h3><p>Groups, roles, users, or everyone</p></div><Button variant="ghost" onClick={() => setEntries([...entries, { subjectType: "group", subjectValue: "", permission: "view" }])}><Plus /> Add rule</Button></div>
      {entries.map((entry, index) => <div className="access-rule" key={index}>
        <select value={entry.subjectType} onChange={(e) => setEntries(entries.map((item, i) => i === index ? { ...item, subjectType: e.target.value } : item))}><option value="group">OIDC group</option><option value="realm_role">Realm role</option><option value="client_role">Client role</option><option value="system_role">System role</option><option value="user">User</option><option value="everyone">Everyone</option></select>
        <input disabled={entry.subjectType === "everyone"} value={entry.subjectValue} onChange={(e) => setEntries(entries.map((item, i) => i === index ? { ...item, subjectValue: e.target.value } : item))} placeholder="/team/writers" />
        <select value={entry.permission} onChange={(e) => setEntries(entries.map((item, i) => i === index ? { ...item, permission: e.target.value } : item))}><option>view</option><option>comment</option><option>edit</option><option>publish</option><option>admin</option></select>
        <Button variant="icon" onClick={() => setEntries(entries.filter((_, i) => i !== index))}><Trash2 /></Button>
      </div>)}
    </div>
    <footer><Button variant="ghost" onClick={close}>Cancel</Button><Button onClick={save}><Save /> Save access</Button></footer>
  </aside></div>;
}

function ThemesPage() {
  const [theme, setTheme] = useState(
    localStorage.getItem("storyweave-theme") || "midnight",
  );
  const choose = (id) => {
    setTheme(id);
    localStorage.setItem("storyweave-theme", id);
    document.documentElement.dataset.theme = id;
  };
  return (
    <div className="page">
      <PageHeader
        eyebrow="PERSONALIZATION"
        title="Themes"
        description="Dark by default, with a preset for every late-night writing mood."
      />
      <div className="theme-grid">
        {presets.map((preset) => (
          <button
            className={`theme-option ${preset.id} ${theme === preset.id ? "selected" : ""}`}
            onClick={() => choose(preset.id)}
            key={preset.id}
          >
            <div>
              <span />
              <span />
              <span />
            </div>
            <footer>
              <span>
                <strong>{preset.name}</strong>
                <small>
                  {preset.mode === "dark" ? (
                    <>
                      <Moon /> Dark
                    </>
                  ) : (
                    <>
                      <Sun /> Light
                    </>
                  )}
                </small>
              </span>
              <i style={{ background: preset.accent }} />
              {theme === preset.id && <Check />}
            </footer>
          </button>
        ))}
      </div>
    </div>
  );
}

function FunctionalAdminPage({ session }) {
  const [tab, setTab] = useState("tenants");
  const tabs = [["tenants", Network, "Tenant settings"], ["roles", Shield, "Roles & mappings"], ["oidc", KeyRound, "OIDC & SSO"], ["security", LockKeyhole, "Encryption"], ["audit", Activity, "Audit log"]];
  return <div className="page">
    <PageHeader eyebrow="SYSTEM" title="Administration" description="Live tenant, identity, authorization, security, and audit controls." />
    <div className="admin-tabs">{tabs.map(([key, Icon, label]) => <button className={tab === key ? "active" : ""} onClick={() => setTab(key)} key={key}><Icon />{label}</button>)}</div>
    {tab === "tenants" && <TenantSettings session={session} />}
    {tab === "roles" && <RoleMappingSettings />}
    {tab === "oidc" && <OidcSettings />}
    {tab === "security" && <EncryptionSettings session={session} />}
    {tab === "audit" && <AuditLog />}
  </div>;
}

function TenantSettings({ session }) {
  const [tenant, setTenant] = useState(null), [platform, setPlatform] = useState(false), [tenants, setTenants] = useState([]), [message, setMessage] = useState("");
  const [creating, setCreating] = useState(false), [create, setCreate] = useState({ name: "", slug: "", canonicalHostname: "", defaultTheme: "midnight", issuer: "", clientId: "storyweave", clientSecret: "" });
  const load = async () => { const data = await api("/admin/tenant"); const canonical = data.tenant.hostnames.find((item) => item.is_canonical)?.hostname || ""; setTenant({ ...data.tenant, canonicalHostname: canonical, aliases: data.tenant.hostnames.filter((item) => !item.is_canonical).map((item) => item.hostname).join("\n") }); setPlatform(data.platformAdmin); if (data.platformAdmin) setTenants((await api("/admin/tenants")).tenants); };
  useEffect(() => { load().catch((cause) => setMessage(cause.message)); }, []);
  if (!tenant) return <section className="admin-card"><div className="empty-state">Loading tenant settings…</div></section>;
  const save = async (e) => { e.preventDefault(); setMessage(""); try { await api("/admin/tenant", { method: "PUT", body: JSON.stringify({ name: tenant.name, slug: tenant.slug, defaultTheme: tenant.default_theme, canonicalHostname: tenant.canonicalHostname, aliases: tenant.aliases.split("\n").map((v) => v.trim()).filter(Boolean), status: tenant.status }) }); setMessage("Tenant settings saved."); load(); } catch (cause) { setMessage(cause.message); } };
  const createTenant = async (e) => { e.preventDefault(); setMessage(""); try { await api("/admin/tenants", { method: "POST", body: JSON.stringify(create) }); setCreating(false); setMessage("Tenant created."); load(); } catch (cause) { setMessage(cause.message); } };
  return <section className="admin-card form-card">
    <header><div><h2>Current tenant</h2><p>Host routing, identity boundary, status, and default theme.</p></div>{platform && <Button onClick={() => setCreating(true)}><Plus /> New tenant</Button>}</header>
    <form onSubmit={save}><div className="settings-grid">
      <label>Name<input value={tenant.name} onChange={(e) => setTenant({ ...tenant, name: e.target.value })} /></label>
      <label>Slug<input value={tenant.slug} onChange={(e) => setTenant({ ...tenant, slug: slugify(e.target.value) })} /></label>
      <label>Canonical hostname<input value={tenant.canonicalHostname} onChange={(e) => setTenant({ ...tenant, canonicalHostname: e.target.value })} /></label>
      <label>Alias hostnames, one per line<textarea value={tenant.aliases} onChange={(e) => setTenant({ ...tenant, aliases: e.target.value })} /></label>
      <label>Default theme<select value={tenant.default_theme} onChange={(e) => setTenant({ ...tenant, default_theme: e.target.value })}>{presets.map((theme) => <option value={theme.id} key={theme.id}>{theme.name}</option>)}</select></label>
      {platform && <label>Status<select value={tenant.status} onChange={(e) => setTenant({ ...tenant, status: e.target.value })}><option value="active">Active</option><option value="suspended">Suspended</option></select></label>}
    </div>{message && <div className="settings-message">{message}</div>}<footer><Button type="submit"><Save /> Save tenant settings</Button></footer></form>
    {platform && <div className="role-catalog"><h3>Platform tenants</h3><div>{tenants.map((item) => <span key={item.id}><Network /><strong>{item.name}</strong><small>{item.canonicalHostname} · {item.status}</small></span>)}</div></div>}
    {creating && <FormDialog title="Create tenant" submitLabel="Create tenant" onSubmit={createTenant} close={() => setCreating(false)} error={message}>
      <label>Name<input required value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value, slug: slugify(e.target.value) })} /></label><label>Slug<input required value={create.slug} onChange={(e) => setCreate({ ...create, slug: slugify(e.target.value) })} /></label>
      <label>Canonical hostname<input required value={create.canonicalHostname} onChange={(e) => setCreate({ ...create, canonicalHostname: e.target.value })} /></label><label>Issuer URL<input type="url" required value={create.issuer} onChange={(e) => setCreate({ ...create, issuer: e.target.value })} /></label>
      <label>Client ID<input required value={create.clientId} onChange={(e) => setCreate({ ...create, clientId: e.target.value })} /></label><label>Client secret<input type="password" value={create.clientSecret} onChange={(e) => setCreate({ ...create, clientSecret: e.target.value })} /></label>
    </FormDialog>}
  </section>;
}

function RoleMappingSettings() {
  const [data, setData] = useState({ roles: [], mappings: [] }), [adding, setAdding] = useState(false), [error, setError] = useState("");
  const [form, setForm] = useState({ systemRoleKey: "tenant-admin", sourceType: "group", sourceValue: "", priority: 100 });
  const load = () => api("/admin/roles").then(setData).catch((cause) => setError(cause.message));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); try { await api("/admin/role-mappings", { method: "POST", body: JSON.stringify(form) }); setAdding(false); setForm({ ...form, sourceValue: "" }); load(); } catch (cause) { setError(cause.message); } };
  const remove = async (id) => { await api(`/admin/role-mappings/${id}`, { method: "DELETE" }); load(); };
  return <section className="admin-card"><header><div><h2>System role mappings</h2><p>Map token claims to server-enforced Storyweave roles.</p></div><Button onClick={() => setAdding(!adding)}><Plus /> New mapping</Button></header>
    {adding && <form className="inline-admin-form" onSubmit={add}><select value={form.systemRoleKey} onChange={(e) => setForm({ ...form, systemRoleKey: e.target.value })}>{data.roles.map((role) => <option value={role.key} key={role.key}>{role.name}</option>)}</select><select value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })}><option value="group">OIDC group</option><option value="realm_role">Realm role</option><option value="client_role">Client role</option><option value="user">User</option></select><input required placeholder="Exact claim value" value={form.sourceValue} onChange={(e) => setForm({ ...form, sourceValue: e.target.value })} /><Button type="submit">Add mapping</Button></form>}
    {error && <div className="error-banner">{error}</div>}<table><thead><tr><th>System role</th><th>Source</th><th>Claim value</th><th /></tr></thead><tbody>{data.mappings.map((mapping) => <tr key={mapping.id}><td><strong>{mapping.system_role_key}</strong></td><td>{mapping.source_type}</td><td><code>{mapping.source_value}</code></td><td><Button variant="icon" onClick={() => remove(mapping.id)}><Trash2 /></Button></td></tr>)}</tbody></table>
  </section>;
}

function OidcSettings() {
  const [config, setConfig] = useState(null), [message, setMessage] = useState("");
  useEffect(() => { api("/admin/oidc").then((data) => setConfig({ issuer: data.config?.issuer || "", clientId: data.config?.client_id || "", clientSecret: "", scopes: data.config?.scopes || "openid profile email", groupsClaim: data.config?.groups_claim || "groups", rolesClaim: data.config?.roles_claim || "realm_access.roles", enabled: Boolean(data.config?.enabled) })).catch((cause) => setMessage(cause.message)); }, []);
  if (!config) return <section className="admin-card"><div className="empty-state">Loading OIDC settings…</div></section>;
  const save = async (e) => { e.preventDefault(); try { await api("/admin/oidc", { method: "PUT", body: JSON.stringify(config) }); setMessage("OIDC settings saved. Existing sessions remain valid."); } catch (cause) { setMessage(cause.message); } };
  const callback = `${location.origin}/auth/callback`, logout = `${location.origin}/auth/backchannel-logout`;
  return <section className="admin-card form-card"><header><div><h2>OIDC and SSO</h2><p>Groups are token claims—not requested OAuth scopes.</p></div><Badge tone={config.enabled ? "success" : ""}>{config.enabled ? "Enabled" : "Disabled"}</Badge></header><form onSubmit={save}>
    <div className="settings-grid"><label>Issuer URL<input type="url" required value={config.issuer} onChange={(e) => setConfig({ ...config, issuer: e.target.value })} /></label><label>Client ID<input required value={config.clientId} onChange={(e) => setConfig({ ...config, clientId: e.target.value })} /></label><label>Client secret<input type="password" placeholder="Leave blank to keep current" value={config.clientSecret} onChange={(e) => setConfig({ ...config, clientSecret: e.target.value })} /></label><label>Scopes<input value={config.scopes} onChange={(e) => setConfig({ ...config, scopes: e.target.value })} /><span className="field-help">Unsupported <code>groups</code> is removed automatically.</span></label><label>Groups claim<input value={config.groupsClaim} onChange={(e) => setConfig({ ...config, groupsClaim: e.target.value })} /></label><label>Roles claim<input value={config.rolesClaim} onChange={(e) => setConfig({ ...config, rolesClaim: e.target.value })} /></label><label className="check-field"><input type="checkbox" checked={config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} /> Enable sign-in</label></div>
    {[['Callback URL', callback], ['Backchannel logout', logout]].map(([label, value]) => <div className="endpoint-card" key={label}><KeyRound /><div><strong>{label}</strong><code>{value}</code></div><Button variant="ghost" onClick={() => navigator.clipboard.writeText(value)}>Copy</Button></div>)}
    {message && <div className="settings-message">{message}</div>}<footer><Button type="submit"><Save /> Save OIDC settings</Button></footer></form></section>;
}

function EncryptionSettings({ session }) {
  const [message, setMessage] = useState(""); const platform = session.systemRoles?.includes("platform-admin");
  const rotate = async () => { try { const result = await api("/admin/rotate-keys", { method: "POST" }); setMessage(result.rotated ? "A new active key was created." : "The active key is already current."); } catch (cause) { setMessage(cause.message); } };
  return <section className="admin-card"><header><div><h2>Encryption key management</h2><p>AES-256-GCM envelope encryption with system-managed rotation.</p></div>{platform && <Button onClick={rotate}><KeyRound /> Rotate now</Button>}</header><div className="security-hero"><div className="key-orbit"><LockKeyhole /><span /><span /></div><div><Badge tone="success"><Check /> HEALTHY</Badge><h2>System-managed keyring</h2><p>Automatic rotation follows the configured 30–90 day interval. Retired keys remain available for authenticated decryption.</p>{message && <div className="settings-message">{message}</div>}</div></div></section>;
}

function AuditLog() {
  const [events, setEvents] = useState([]), [error, setError] = useState("");
  const load = () => api("/admin/audit").then((data) => setEvents(data.events)).catch((cause) => setError(cause.message));
  useEffect(() => { load(); }, []);
  return <section className="admin-card"><header><div><h2>Audit log</h2><p>The 200 most recent attributed tenant events.</p></div><Button variant="ghost" onClick={load}><Activity /> Refresh</Button></header>{error && <div className="error-banner">{error}</div>}<table><thead><tr><th>Time</th><th>Action</th><th>Target</th><th>Actor</th></tr></thead><tbody>{events.map((event) => <tr key={event.id}><td>{new Date(event.created_at).toLocaleString()}</td><td><code>{event.action}</code></td><td>{event.target_type} · {event.target_id}</td><td>{event.actor_id || "system"}</td></tr>)}</tbody></table>{!events.length && <div className="empty-state">No audit events yet.</div>}</section>;
}

function AdminPage() {
  const [tab, setTab] = useState("roles");
  const mappings = [
    { role: "Tenant Admin", source: "Realm role", value: "storyweave-admin" },
    { role: "Creator", source: "OIDC group", value: "/studios/writers" },
    { role: "Reviewer", source: "Client role", value: "storyweave:reviewer" },
  ];
  return (
    <div className="page">
      <PageHeader
        eyebrow="SYSTEM"
        title="Administration"
        description="Tenant identity, authorization, security, and platform controls."
      />
      <div className="admin-tabs">
        {[
          ["roles", Shield, "Roles & mappings"],
          ["oidc", KeyRound, "OIDC & SSO"],
          ["tenants", Network, "Tenants"],
          ["security", LockKeyhole, "Encryption"],
          ["audit", Activity, "Audit log"],
        ].map(([key, Icon, label]) => (
          <button
            className={tab === key ? "active" : ""}
            onClick={() => setTab(key)}
            key={key}
          >
            <Icon />
            {label}
          </button>
        ))}
      </div>
      {tab === "roles" && (
        <section className="admin-card">
          <header>
            <div>
              <h2>System role mappings</h2>
              <p>
                Map OIDC claims to Storyweave roles. All authorization remains
                server-enforced.
              </p>
            </div>
            <Button>
              <Plus /> New mapping
            </Button>
          </header>
          <table>
            <thead>
              <tr>
                <th>System role</th>
                <th>Identity source</th>
                <th>Claim value</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {mappings.map((mapping) => (
                <tr key={mapping.value}>
                  <td>
                    <strong>{mapping.role}</strong>
                  </td>
                  <td>{mapping.source}</td>
                  <td>
                    <code>{mapping.value}</code>
                  </td>
                  <td>
                    <Badge tone="success">Active</Badge>
                  </td>
                  <td>
                    <MoreHorizontal />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="role-catalog">
            <h3>Built-in system roles</h3>
            <div>
              {[
                "Platform Admin",
                "Tenant Admin",
                "Workspace Admin",
                "Creator",
                "Editor",
                "Reviewer",
                "Viewer",
              ].map((role) => (
                <span key={role}>
                  <Shield />
                  <strong>{role}</strong>
                  <small>Server-enforced permissions</small>
                </span>
              ))}
            </div>
          </div>
        </section>
      )}
      {tab === "oidc" && <OidcAdmin />}
      {tab === "security" && <SecurityAdmin />}
      {!["roles", "oidc", "security"].includes(tab) && (
        <section className="admin-card empty-state">
          <Settings />
          <h2>
            {tab === "tenants" ? "Tenant management" : "Auditable by design"}
          </h2>
          <p>
            {tab === "tenants"
              ? "Create tenants, bind hostnames, select canonical URLs, and isolate identity clients."
              : "Every access change, publish, role mapping, login, and key rotation is attributed and encrypted."}
          </p>
        </section>
      )}
    </div>
  );
}
function OidcAdmin() {
  return (
    <section className="admin-card form-card">
      <header>
        <div>
          <h2>OIDC and session behavior</h2>
          <p>
            Keycloak-compatible defaults follow the shared EdgeFox session
            standard.
          </p>
        </div>
        <Badge tone="success">
          <Check /> Connected
        </Badge>
      </header>
      <div className="settings-grid">
        <label>
          Issuer URL
          <input defaultValue="https://auth.example.com/realms/studio" />
        </label>
        <label>
          Client ID
          <input defaultValue="storyweave" />
        </label>
        <label>
          Scopes
          <input defaultValue="openid profile email" />
          <span className="field-help">Group membership comes from the <code>groups</code> token claim, not an OAuth scope.</span>
        </label>
        <label>
          Groups claim
          <input defaultValue="groups" />
        </label>
        <label>
          Realm roles claim
          <input defaultValue="realm_access.roles" />
        </label>
        <label>
          Username claim
          <input defaultValue="preferred_username" />
        </label>
      </div>
      <div className="endpoint-card">
        <KeyRound />
        <div>
          <strong>Backchannel logout endpoint</strong>
          <code>{location.origin}/auth/backchannel-logout</code>
        </div>
        <Button variant="ghost">Copy</Button>
      </div>
      <ul className="check-list">
        <li>
          <Check /> PKCE authorization code flow and one-shot passive SSO
        </li>
        <li>
          <Check /> Eight-hour absolute server-side sessions
        </li>
        <li>
          <Check /> RP-initiated logout with encrypted ID-token hint
        </li>
        <li>
          <Check /> Signed, replay-protected backchannel logout by sid or sub
        </li>
        <li>
          <Check /> Authenticated SSE invalidation and 120-second polling
          fallback
        </li>
        <li>
          <Check /> <code>restricted-account</code> overrides all permissions
        </li>
      </ul>
      <footer>
        <Button>
          <Save /> Save OIDC settings
        </Button>
      </footer>
    </section>
  );
}
function SecurityAdmin() {
  return (
    <section className="admin-card">
      <header>
        <div>
          <h2>Encryption key management</h2>
          <p>
            Application data and sensitive identity material use envelope
            AES-256-GCM encryption.
          </p>
        </div>
        <Button>
          <KeyRound /> Rotate now
        </Button>
      </header>
      <div className="security-hero">
        <div className="key-orbit">
          <LockKeyhole />
          <span />
          <span />
        </div>
        <div>
          <Badge tone="success">
            <Check /> HEALTHY
          </Badge>
          <h2>System-managed keyring</h2>
          <p>
            The active key rotates automatically every 60 days. Retired keys
            remain available for decryption while background re-encryption
            advances records to the current key.
          </p>
        </div>
      </div>
      <div className="security-stats">
        <span>
          <small>ALGORITHM</small>
          <strong>AES-256-GCM</strong>
        </span>
        <span>
          <small>ROTATION</small>
          <strong>60 days</strong>
        </span>
        <span>
          <small>KEY STORAGE</small>
          <strong>Outside database</strong>
        </span>
        <span>
          <small>FILE MODE</small>
          <strong>0600</strong>
        </span>
      </div>
    </section>
  );
}

function DocsPage() {
  const [section, setSection] = useState("overview");
  const sections = [
    ["overview", "Overview"],
    ["getting-started", "Getting started"],
    ["workspaces", "Workspaces"],
    ["map", "Story map"],
    ["passages", "Writing passages"],
    ["variables", "Variables & logic"],
    ["testing", "Testing & publishing"],
    ["access", "Sharing & access"],
    ["themes", "Themes"],
  ];
  return (
    <div className="docs-page">
      <aside>
        <div className="docs-title">
          <BookOpen />
          <span>
            <strong>Storyweave Docs</strong>
            <small>Version 1.2.0</small>
          </span>
        </div>
        {sections.map(([key, label]) => (
          <button
            className={section === key ? "active" : ""}
            onClick={() => setSection(key)}
            key={key}
          >
            {label}
          </button>
        ))}
      </aside>
      <article>
        <Badge>DOCUMENTATION</Badge>
        <h1>{sections.find(([key]) => key === section)?.[1]}</h1>
        <EndUserDocsContent section={section} />
      </article>
    </div>
  );
}

function EndUserDocsContent({ section }) {
  const content = {
    overview: <>
      <p className="lead">Storyweave is a collaborative editor for branching, interactive stories. Build passages on a visual map, connect them with choices, remember reader decisions with variables, test every path, and publish a stable reader version.</p>
      <div className="feature-doc-grid">
        <span><Workflow /><strong>Visual story map</strong><p>Arrange passages, see connections, and spot endings or disconnected paths.</p></span>
        <span><FileText /><strong>Focused writing</strong><p>Edit passage text, titles, tags, choices, reader appearance, and story logic together.</p></span>
        <span><Braces /><strong>Structured logic</strong><p>Build conditions and variable changes with guided controls instead of expression syntax.</p></span>
        <span><Play /><strong>Test before publishing</strong><p>Play through the current draft, then publish an explicit version when it is ready.</p></span>
      </div>
      <div className="docs-callout"><Cloud /><span><strong>Autosave is built in.</strong> Changes save after a short pause. Wait for “All changes saved” before closing the editor.</span></div>
    </>,
    "getting-started": <>
      <p className="lead">A story belongs to a workspace. Start there, create the story, then build outward from its first passage.</p>
      <h2>Create your first story</h2>
      <ol>
        <li>Open <strong>Workspaces</strong> and choose a workspace you can edit.</li>
        <li>Select <strong>New story</strong>, enter its title, slug, and reader theme.</li>
        <li>Open the starting passage and replace its title and body.</li>
        <li>Add passages with the plus button and connect them from the <strong>Logic</strong> panel.</li>
        <li>Use <strong>Preview</strong> to play the draft. Use <strong>Publish</strong> only when the current saved version is ready for readers.</li>
      </ol>
      <div className="docs-callout warning"><Save /><span><strong>Publish is not autosave.</strong> Autosave protects the draft; publishing creates the reader snapshot. Later edits remain drafts until you publish again.</span></div>
    </>,
    workspaces: <>
      <p className="lead">Workspaces organize stories by project or team and provide the access rules stories can inherit.</p>
      <h2>Workspace visibility</h2>
      <table><thead><tr><th>Setting</th><th>Meaning</th></tr></thead><tbody>
        <tr><td><strong>Private</strong></td><td>Only the owner, administrators, and identities named by an access rule can open it.</td></tr>
        <tr><td><strong>Entire tenant</strong></td><td>Signed-in members of the current tenant can discover the workspace.</td></tr>
      </tbody></table>
      <h2>Finding stories</h2><p>Use workspace search to filter by title and the visibility filter to narrow the table. Home shows your actual recently updated stories—there are no sample or placeholder stories.</p>
    </>,
    map: <>
      <p className="lead">The story map shows each passage as a card and each choice as a connecting line.</p>
      <table><thead><tr><th>Action</th><th>Control</th></tr></thead><tbody>
        <tr><td>Move around the canvas</td><td>Drag an empty area of the map.</td></tr>
        <tr><td>Move a passage</td><td>Drag its passage card.</td></tr>
        <tr><td>Connect passages</td><td>Click the connector handle on the source card, then click the destination card.</td></tr>
        <tr><td>Passage actions</td><td>Right-click a passage card or use the three-dot button in the inspector.</td></tr>
        <tr><td>Add at a position</td><td>Right-click empty map space and select <strong>New passage here</strong>.</td></tr>
        <tr><td>Zoom</td><td>Use the mouse wheel over the map or the toolbar’s plus/minus controls.</td></tr>
        <tr><td>Reset the view</td><td>Select the percentage shown between the zoom buttons.</td></tr>
        <tr><td>Undo or redo</td><td>Use the arrow controls in the map toolbar.</td></tr>
        <tr><td>Find a passage</td><td>Use the search field above the passage list.</td></tr>
      </tbody></table>
      <p>The first passage is marked as the starting point. A passage with no available outgoing choices behaves as an ending during testing. Collaborator avatars appear beside the passage they are currently editing; the editor list in the header shows their exact passage and panel.</p>
    </>,
    passages: <>
      <p className="lead">Select a passage from the list or map, then use the inspector on the right.</p>
      <h2>Write</h2><p>Edit the passage name and body with safe Markdown. Preview and published stories render headings, bold and italic text, links, lists, blockquotes, inline and fenced code, horizontal rules, and tables. The toolbar inserts common Markdown, and <strong>Preview Markdown</strong> renders it directly in the inspector.</p>
      <h2>Passage actions</h2><p>Right-click a passage in the list or map to edit it, edit its choices, duplicate it, make it the story’s starting passage, or delete it. Deleting a passage also removes choices from other passages that targeted it. Storyweave keeps at least one passage and asks for confirmation before deletion.</p>
      <h2>Choices</h2><p>Click the connector handle on the first passage and then click the destination card. Storyweave creates the choice and opens it in <strong>Logic</strong> so you can change its label, condition, and variable effect. You can also add choices directly in Logic. Deleting a choice removes only that connection.</p>
      <h2>Design</h2><p>Select the reader theme and transition used by Preview and the published reader. Interface themes and reader themes are separate.</p>
    </>,
    variables: <>
      <p className="lead">Variables store reader state. Conditions decide whether a choice is shown, while effects change state after a reader selects a choice.</p>
      <table><thead><tr><th>Type</th><th>Useful for</th><th>Available logic</th></tr></thead><tbody>
        <tr><td>Boolean</td><td>Flags such as whether a door was opened</td><td>True, false, set, or toggle</td></tr>
        <tr><td>Number</td><td>Trust, health, scores, or counters</td><td>Compare, set, increase, or decrease</td></tr>
        <tr><td>String</td><td>Names, states, or a selected route</td><td>Equals, differs, contains, or set</td></tr>
        <tr><td>List</td><td>Inventory, clues, or visited locations</td><td>Contains, excludes, add item, or remove item</td></tr>
      </tbody></table>
      <h2>Add a condition</h2><ol><li>Create the variable in <strong>Variables</strong> and choose a sensible default.</li><li>Open the source passage’s <strong>Logic</strong> panel.</li><li>Change Availability to <strong>Only when a variable matches</strong>.</li><li>Select the variable, a type-appropriate rule, and a value if the rule needs one.</li></ol>
      <h2>Change a variable</h2><p>Under <strong>When selected</strong>, choose the variable and action. The effect runs before the player opens the target passage, so its choices immediately see the new value.</p>
      <div className="docs-callout"><Braces /><span><strong>No expression language is required.</strong> Storyweave stores structured rules built by the controls shown in the editor.</span></div>
    </>,
    testing: <>
      <p className="lead">Preview runs the current draft. Publish stores the exact saved document as the version readers receive.</p>
      <h2>Preview checklist</h2><ul><li>Every choice reaches the intended passage.</li><li>Conditional choices appear and disappear after the correct earlier decisions.</li><li>Variable effects use the intended operation and value.</li><li>All endings provide the expected restart behavior.</li><li>The reader theme remains legible on desktop and mobile.</li></ul>
      <h2>Publishing</h2><p>The editor header always shows the current shared published version. Select <strong>Publish changes</strong> to replace the reader snapshot with the newest saved version. Every open collaborator receives the new published version live; returning to the tab refreshes it from the server. <strong>Open reader</strong> opens that shared snapshot in a new tab.</p>
      <div className="docs-callout warning"><Cloud /><span><strong>Remote update available</strong> means another editor saved a newer version. Load it before continuing so you do not publish an outdated draft.</span></div>
    </>,
    access: <>
      <p className="lead">Access is managed at workspace and story level. A story can inherit its workspace or use a narrower rule set.</p>
      <table><thead><tr><th>Permission</th><th>Allows</th></tr></thead><tbody>
        <tr><td>View</td><td>Open and read the story.</td></tr><tr><td>Comment</td><td>View and participate in review workflows.</td></tr><tr><td>Edit</td><td>Change passages, logic, variables, and design.</td></tr><tr><td>Publish</td><td>Edit and create a published reader snapshot.</td></tr><tr><td>Admin</td><td>Manage access rules in addition to story work.</td></tr>
      </tbody></table>
      <h2>Identity rules</h2><p>Authorized editors can grant access to an exact OIDC group, realm role, client role, Storyweave system role, or user. Values must match the identity value supplied by your organization.</p>
      <p>If a workspace or story is missing, ask its owner or an administrator to verify both the visibility setting and your identity rule.</p>
    </>,
    themes: <>
      <p className="lead">Storyweave uses dark mode by default and includes multiple dark and light presets.</p>
      <h2>Interface theme</h2><p>Open <strong>Themes</strong> or choose <strong>Appearance</strong> from the profile menu. Your selection is saved in this browser.</p>
      <h2>Reader theme</h2><p>Inside a story, open <strong>Design</strong> to choose the appearance of Preview and the published reader. Changing your personal interface theme does not alter a story’s reader theme.</p>
    </>,
  };
  return content[section] || content.overview;
}
function DocsContent({ section }) {
  const content = {
    overview: (
      <>
        <p className="lead">
          Storyweave is a multi-tenant, collaborative interactive-fiction studio
          built for modern teams. It combines a visual story graph, rich passage
          editor, variables, testing, publishing, workspaces, and enterprise
          identity controls.
        </p>
        <div className="docs-callout">
          <Sparkles />
          <span>
            <strong>Dark-first by design</strong> Midnight is the default
            interface theme, with eleven additional presets available per tenant
            and per user.
          </span>
        </div>
        <h2>Core capabilities</h2>
        <div className="feature-doc-grid">
          <span>
            <Workflow />
            <strong>Visual story map</strong>
            <p>See branches, endings, and disconnected passages at a glance.</p>
          </span>
          <span>
            <Users />
            <strong>Live collaboration</strong>
            <p>
              Presence, autosave, SSE updates, comments, and attributed
              versions.
            </p>
          </span>
          <span>
            <Braces />
            <strong>Stateful stories</strong>
            <p>
              Booleans, numbers, strings, lists, conditions, and reader state.
            </p>
          </span>
          <span>
            <Shield />
            <strong>Enterprise access</strong>
            <p>
              Tenant, workspace, and story rules for users, groups, and roles.
            </p>
          </span>
        </div>
      </>
    ),
    stories: (
      <>
        <p className="lead">
          Stories live inside workspaces and contain passages connected by
          labeled choices.
        </p>
        <h2>Passages</h2>
        <p>
          Every passage has a stable ID, title, body, tags, layout position,
          choices, conditions, and optional presentation settings. Autosave runs
          after a brief pause and creates attributed versions. Live events deliver immediately, with a two-second version check as a safety net for proxy-buffered or missed SSE messages.
        </p>
        <p>
          The map supports cursor-anchored wheel zoom, drag-to-pan, draggable
          passage cards, reset zoom, undo, redo, search, and live link updates.
        </p>
        <h2>Publishing</h2>
        <p>
          Publish permissions are separate from edit permissions. Story access
          is checked before reader content is decrypted or exported.
        </p>
      </>
    ),
    variables: (
      <>
        <p className="lead">
          Variables remember reader choices and power conditional branches.
        </p>
        <p>
          Conditions use a typed builder: choose a variable, then an operator
          valid for that variable type, then a value where required. The test
          player evaluates the same structured rule saved with the story.
        </p>
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Use</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Boolean</td>
              <td>Flags and one-time events</td>
              <td>
                <code>has_key == true</code>
              </td>
            </tr>
            <tr>
              <td>Number</td>
              <td>Stats, scores, and counters</td>
              <td>
                <code>courage &gt;= 3</code>
              </td>
            </tr>
            <tr>
              <td>String</td>
              <td>Names and current state</td>
              <td>
                <code>faction == "fox"</code>
              </td>
            </tr>
            <tr>
              <td>List</td>
              <td>Inventory and collected clues</td>
              <td>
                <code>inventory contains "radio"</code>
              </td>
            </tr>
          </tbody>
        </table>
      </>
    ),
    workspaces: (
      <>
        <p className="lead">
          Workspaces are secure containers for related stories, members,
          defaults, and publishing policies.
        </p>
        <h2>Visibility</h2>
        <p>
          New workspaces are private. Administrators grant access to users, OIDC
          groups, realm roles, client roles, or Storyweave system roles. Stories
          can inherit workspace access or define stricter rules.
        </p>
      </>
    ),
    access: (
      <>
        <p className="lead">
          Authorization is deny-by-default and enforced on the server before
          data loading, decryption, exports, publishing, and realtime
          subscriptions.
        </p>
        <h2>Permissions</h2>
        <p>
          <code>view → comment → edit → publish → admin → owner</code>
        </p>
        <div className="docs-callout warning">
          <Shield />
          <span>
            <strong>Restricted accounts</strong> The exact trusted{" "}
            <code>restricted-account</code> realm role overrides every
            administrator, owner, workspace, and story permission.
          </span>
        </div>
      </>
    ),
    roles: (
      <>
        <p className="lead">
          System roles translate external identity claims into stable
          application permissions.
        </p>
        <table>
          <thead>
            <tr>
              <th>Role</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            {[
              [
                "Platform Admin",
                "All tenants, key rotation, platform settings",
              ],
              [
                "Tenant Admin",
                "Tenant identity, roles, workspaces, and policies",
              ],
              [
                "Workspace Admin",
                "Workspace membership, defaults, and stories",
              ],
              ["Creator", "Create and own stories"],
              ["Editor", "View, edit, and comment"],
              ["Reviewer", "View and comment"],
              ["Viewer", "Read-only access"],
            ].map(([a, b]) => (
              <tr key={a}>
                <td>
                  <strong>{a}</strong>
                </td>
                <td>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    ),
    oidc: (
      <>
        <p className="lead">
          Each tenant has an isolated OIDC issuer/client boundary and canonical
          hostname.
        </p>
        <ul>
          <li>Authorization code flow with PKCE and nonce validation</li>
          <li>
            One-shot <code>prompt=none</code> passive SSO without redirect loops
          </li>
          <li>Server-side encrypted tokens and atomic refresh rotation</li>
          <li>Hard eight-hour non-sliding local session maximum</li>
          <li>
            RP-initiated logout with <code>id_token_hint</code>
          </li>
          <li>
            Signed backchannel logout validated by issuer, audience, event,
            time, JTI, SID/SUB, and tenant
          </li>
          <li>
            Authenticated same-origin SSE invalidation plus focus/polling
            fallback
          </li>
          <li>
            Inert <code>/logged-out</code> page with no links, scripts, probes,
            or redirects
          </li>
        </ul>
      </>
    ),
    encryption: (
      <>
        <p className="lead">
          Story documents, version snapshots, OIDC secrets, token sets, claims,
          audit details, and workspace descriptions use AES-256-GCM
          authenticated encryption.
        </p>
        <h2>Key lifecycle</h2>
        <p>
          The keyring is stored outside SQLite with mode <code>0600</code>.
          Rotation is system-managed and configurable from 30 to 90 days. Each
          envelope records its algorithm, key ID, 96-bit IV, authentication tag,
          and ciphertext. Retired keys remain available for safe reads.
        </p>
      </>
    ),
    deploy: (
      <>
        <p className="lead">
          The root archive is ready for Node 22 on Pterodactyl.
        </p>
        <pre>
          <code>npm install{"\n"}npm start</code>
        </pre>
        <p>
          No <code>.env</code> file is required. The server binds to{" "}
          <code>0.0.0.0</code> and checks <code>SERVER_PORT</code>,{" "}
          <code>P_SERVER_PORT</code>, <code>PRIMARY_PORT</code>,{" "}
          <code>PORT</code>, and <code>APP_PORT</code> before falling back to
          3000.
        </p>
        <h2>Persistent paths</h2>
        <p>
          Back up <code>data/storyweave.sqlite</code>,{" "}
          <code>data/keys/keyring.json</code>, and <code>data/config.json</code>{" "}
          together. Never restore the encrypted database without its matching
          keyring.
        </p>
      </>
    ),
  };
  return content[section];
}

export default function App() {
  const [booting, setBooting] = useState(true),
    [setupRequired, setSetupRequired] = useState(false),
    [session, setSession] = useState(null);
  const refreshSession = async () => {
    try {
      setSession(await api("/session"));
    } catch {
      setSession({ authenticated: false });
    }
  };
  useEffect(() => {
    const theme = localStorage.getItem("storyweave-theme") || "midnight";
    document.documentElement.dataset.theme = theme;
    Promise.all([api("/setup/status"), api("/session")])
      .then(([setup, current]) => {
        setSetupRequired(setup.required);
        setSession(current);
      })
      .finally(() => setBooting(false));
  }, []);
  if (booting) return <BootScreen />;
  const loginQuery = new URLSearchParams(location.search);
  const loginReturnTo = loginQuery.get("returnTo");
  const safeLoginReturnTo = loginReturnTo?.startsWith("/") && !loginReturnTo.startsWith("//") ? loginReturnTo : "/";
  return (
    <Routes>
      <Route
        path="/setup"
        element={setupRequired ? <SetupPage /> : <Navigate to="/" replace />}
      />
      <Route path="/setup/oidc-recovery" element={<OidcRecoveryPage />} />
      <Route
        path="/login"
        element={
          !setupRequired && !session?.authenticated ? (
            <LoginPage session={session} />
          ) : (
            <Navigate to={safeLoginReturnTo} replace />
          )
        }
      />
      {setupRequired ? (
        <Route path="*" element={<Navigate to="/setup" replace />} />
      ) : !session?.authenticated ? (
        <Route path="*" element={<Navigate to="/login" replace />} />
      ) : (
        <Route
          path="*"
          element={
            <Shell session={session} refreshSession={refreshSession}>
              <Routes>
                <Route path="/" element={<HomePage session={session} />} />
                <Route path="/workspaces" element={<WorkspacesPage />} />
                <Route
                  path="/workspaces/:workspaceId"
                  element={<WorkspacePage />}
                />
                <Route path="/stories/:storyId" element={<EditorPage session={session} />} />
                <Route path="/read/:storyId" element={<PublishedReader />} />
                <Route path="/themes" element={<ThemesPage />} />
                <Route path="/admin" element={<FunctionalAdminPage session={session} />} />
                <Route path="/docs" element={<DocsPage />} />
                <Route
                  path="/recent"
                  element={<HomePage session={session} />}
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Shell>
          }
        />
      )}
    </Routes>
  );
}
